/**
 * Nokuhiro i18n translations
 * Languages: English, German, Spanish (Castilian), Italian, French
 */

export type Locale = "en" | "de" | "es" | "it" | "fr";

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  es: "Español",
  it: "Italiano",
  fr: "Français",
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  en: "🇬🇧",
  de: "🇩🇪",
  es: "🇪🇸",
  it: "🇮🇹",
  fr: "🇫🇷",
};

export interface Translations {
  // Navbar
  nav: {
    features: string;
    howItWorks: string;
    companions: string;
    science: string;
    pricing: string;
    download: string;
    downloadNow: string;
  };
  // Beta Sign-Up
  betaSignup: {
    heading1: string;
    heading2: string;
    desc: string;
    emailPlaceholder: string;
    platformLabel: string;
    ios: string;
    android: string;
    both: string;
    submitBtn: string;
    successMsg: string;
    alreadyRegistered: string;
    errorMsg: string;
  };
  // Hero
  hero: {
    tagline: string;
    heading1: string;
    heading2: string;
    subtitle: string;
    cta1: string;
    cta2: string;
    badge1: string;
    badge2: string;
    badge3: string;
    badge4: string;
  };
  // App Preview
  preview: {
    label: string;
    heading1: string;
    heading2: string;
    desc: string;
    feature1Title: string;
    feature1Desc: string;
    feature2Title: string;
    feature2Desc: string;
    feature3Title: string;
    feature3Desc: string;
  };
  // Features
  features: {
    label: string;
    heading: string;
    subtitle: string;
    items: Array<{ title: string; desc: string }>;
  };
  // How It Works
  howItWorks: {
    label: string;
    heading: string;
    subtitle: string;
    steps: Array<{ title: string; desc: string }>;
  };
  // Companions
  companions: {
    label: string;
    heading: string;
    subtitle: string;
    evolutionTitle: string;
    stages: string[];
    animals: string[];
    styles: string[];
    traits: string[];
  };
  // Science
  science: {
    label: string;
    heading: string;
    subtitle: string;
    items: Array<{ title: string; source: string; desc: string }>;
    antiAddictionTitle: string;
    antiAddictionDesc: string;
  };
  // Pricing
  pricing: {
    label: string;
    heading: string;
    subtitle: string;
    badge: string;
    alsoAvailable: (code: string) => string;
    oneTime: string;
    items: string[];
    cta: string;
    whyTitle: string;
    whyDesc: string;
  };
  // Download / Beta CTA
  downloadSection: {
    heading1: string;
    heading2: string;
    desc: string;
    languages: string;
  };
  // Privacy
  privacy: {
    label: string;
    heading: string;
    sections: Array<{ title: string; content: string }>;
    contactText: string;
  };
  // Hero (new CRO fields)
  heroCro: {
    heading: string;
    subheading: string;
    ctaIos: string;
    ctaAndroid: string;
    trustLine: string;
  };
  // Trust Bar
  trustBar: {
    ages: string;
    oneTime: string;
    noAds: string;
    quickRoutines: string;
  };
  // How It Works (3-step)
  howItWorksCro: {
    label: string;
    heading: string;
    steps: Array<{ icon: string; title: string; desc: string }>;
  };
  // Product Clarity
  productClarity: {
    label: string;
    heading: string;
    subtitle: string;
    features: Array<{ title: string; desc: string }>;
  };
  // Parent Pain
  parentPain: {
    label: string;
    heading: string;
    beforeTitle: string;
    afterTitle: string;
    before: string[];
    after: string[];
  };
  // Child Motivation
  childMotivation: {
    label: string;
    heading: string;
    subtitle: string;
    items: Array<{ icon: string; title: string; desc: string }>;
  };
  // Parent Value
  parentValue: {
    label: string;
    heading: string;
    benefits: string[];
    quote: string;
    quote2: string;
    quoteAttribution: string;
    quote2Attribution: string;
  };
  // Paid Value Justification
  paidValue: {
    label: string;
    heading: string;
    body: string;
    points: string[];
    cta: string;
  };
  // Science (parent-friendly)
  scienceCro: {
    label: string;
    heading: string;
    body: string;
  };
  // Privacy & Safety
  privacySafety: {
    label: string;
    heading: string;
    items: Array<{ icon: string; title: string; desc: string }>;
  };
  // Founder Story
  founder: {
    label: string;
    heading: string;
    body: string;
  };
  // Use Cases
  useCases: {
    label: string;
    heading: string;
    items: Array<{ title: string; from: string; to: string }>;
  };
  // Proof / Testimonials
  proof: {
    label: string;
    heading: string;
    quote1: string;
    quote1Attribution: string;
    quote2: string;
    quote2Attribution: string;
  };
  // Inline FAQ
  faqCro: {
    label: string;
    heading: string;
    items: Array<{ q: string; a: string }>;
  };
  // Final CTA
  finalCta: {
    heading: string;
    ctaIos: string;
    ctaAndroid: string;
  };
  // Sticky bar
  sticky: {
    label: string;
    ctaIos: string;
    ctaAndroid: string;
  };
  // Footer
  footer: {
    desc: string;
    product: string;
    theScience: string;
    scienceLinks: Array<{ label: string; href: string }>;
    support: string;
    supportLinks: Array<{ label: string; href: string }>;
    followUs: string;
    instagram: string;
    copyright: string;
    coppa: string;
    gdpr: string;
  };
}

const en: Translations = {
  nav: {
    features: "Features",
    howItWorks: "How It Works",
    companions: "Companions",
    science: "Science",
    pricing: "Pricing",
    download: "Download Now",
    downloadNow: "Download Now",
  },
  betaSignup: {
    heading1: "Join the ",
    heading2: "Closed Beta",
    desc: "Nokuhiro is currently in closed beta testing. Sign up to be among the first families to experience the adventure.",
    emailPlaceholder: "Your email address",
    platformLabel: "Which platform?",
    ios: "iOS",
    android: "Android",
    both: "Both",
    submitBtn: "Join the Beta",
    successMsg: "You're in! We'll notify you when Nokuhiro is ready for download.",
    alreadyRegistered: "You're already on the list! We'll be in touch soon.",
    errorMsg: "Something went wrong. Please try again.",
  },
  hero: {
    tagline: "",
    heading1: "Less Nagging.",
    heading2: "More Growing.",
    subtitle: "The #1 habit app for kids aged 3–12. Science-backed routines that children love — no ads, no subscriptions, just one purchase.",
    cta1: "Download Now",
    cta2: "Explore Features",
    badge1: "Only 5 {currency} — No Ads",
    badge2: "One-Time Purchase",
    badge3: "COPPA & GDPR-K Safe",
    badge4: "5 Languages",
  },
  preview: {
    label: "Meet Nokuhiro",
    heading1: "Your Child's",
    heading2: " Heroic Journey Starts Here",
    desc: "Guided by Nokuhiro the lion hero, children create their own hero avatar, choose a companion animal from 11 unique characters, and embark on daily quests. Every habit completed earns XP, levels up their hero, and brings them closer to real-world rewards set by parents.",
    feature1Title: "Nokuhiro — Your Guide",
    feature1Desc: "A brave lion hero who delivers advice, encouragement, and celebrates every win",
    feature2Title: "Quest Map Adventures",
    feature2Desc: "Winding golden path with milestones, companions, and treasure rewards",
    feature3Title: "Adaptive AI Engine",
    feature3Desc: "Dynamically adjusts XP based on mastery and growth — on-device, private",
  },
  features: {
    label: "Powerful Features",
    heading: "Everything a Hero Needs",
    subtitle: "Built on peer-reviewed behavioral science, designed for children, trusted by parents.",
    items: [
      { title: "Quest-Based Habits", desc: "Daily habits become heroic quests on a winding golden path. Children see their progress as a real adventure with regular milestones at key progress points." },
      { title: "Living Companions", desc: "Choose from 11 unique animal companions that grow, react, and evolve based on your child's habits. They show up for their buddy — not just for points." },
      { title: "Adaptive AI Engine", desc: "The on-device AI dynamically adjusts XP values — rewarding effort on neglected habits and fading rewards on mastered ones to build true intrinsic motivation." },
      { title: "Sibling Team Quests", desc: "Siblings pool XP toward shared family rewards. Boss battles, cooperative challenges, and joint celebrations — no toxic competition, only teamwork." },
      { title: "Three Age Modes", desc: "Explorer (3–5) uses big colorful icons and simple interactions. Adventurer (6–8) adds full gamification. Champion (9–12) introduces stats and autonomy. One app, three experiences." },
      { title: "Privacy-First Design", desc: "Server-first architecture with local caching. Zero third-party tracking. COPPA 2026 and GDPR-K compliant. All data encrypted in transit and at rest." },
    ],
  },
  howItWorks: {
    label: "How It Works",
    heading: "From Download to Daily Hero",
    subtitle: "Getting started takes less than two minutes. Here's the journey.",
    steps: [
      { title: "Parents Set Up in 90 Seconds", desc: "Add your child's name and age. Choose from smart habit suggestions with micro-habit defaults. Set a real-world reward and quest duration." },
      { title: "Child Meets Nokuhiro", desc: "Nokuhiro the lion hero welcomes your child into the enchanted forest. They pick their companion animal and complete their first quest — with a massive celebration." },
      { title: "Daily Quests Build Habits", desc: "Each day, children complete habit quests to earn XP, level up their companion, and progress along the golden quest path toward their reward." },
      { title: "Track Progress Together", desc: "Parents monitor progress through the dashboard with insights, weekly reports, and completion stats. Celebrate milestones as a family." },
    ],
  },
  companions: {
    label: "Choose Your Companion",
    heading: "11 Unique Adventure Buddies",
    subtitle: "Each companion has its own personality, voice, and special abilities. They evolve through 5 stages — from Hatchling to Legend.",
    evolutionTitle: "5 Evolution Stages",
    stages: ["Hatchling", "Explorer", "Adventurer", "Champion", "Legend"],
    animals: ["Dinosaur", "Unicorn", "Rabbit", "Fox", "Owl", "Monkey", "Elephant", "Lion", "Panda", "Bear", "Cow"],
    styles: ["Bold & Enthusiastic", "Magical & Dreamy", "Energetic & Fast", "Clever & Strategic", "Wise & Thoughtful", "Adventurous & Fun", "Gentle & Wise", "Brave & Proud", "Playful & Gentle", "Strong & Determined", "Calm & Nurturing"],
    traits: ["Digs up bonus treasures", "Leaves rainbow trail on map", "Races along the quest path", "Finds hidden map shortcuts", "Shares fun facts after habits", "Has a giraffe sidekick", "Never forgets a streak", "Mane glows brighter with streaks", "Does tumbling celebrations", "Shows strength animations", "Grows a flower garden"],
  },
  science: {
    label: "Backed by Research",
    heading: "Science, Not Gimmicks",
    subtitle: "Every feature is rooted in peer-reviewed behavioral science. This isn't a chore checklist with stickers — it's a precision-engineered behavioral system disguised as an adventure game.",
    items: [
      { title: "66-Day Automaticity", source: "Lally et al., 2010", desc: "Research shows habits take a median of 66 days to become automatic. Our quest cycles are calibrated to 28–42 days with regular milestones along the journey." },
      { title: "Tiny Habits Framework", source: "BJ Fogg, Stanford", desc: "Every habit starts micro — under 30 seconds. After 7 consecutive days, the app suggests expanding. Start tiny, build confidence, scale up with parent approval." },
      { title: "Growth Mindset Feedback", source: "Carol Dweck, Stanford", desc: "All praise focuses on effort, never ability. \"You worked so hard!\" builds resilience. \"You're so smart!\" creates fragility. Every text follows this principle." },
      { title: "Overjustification Prevention", source: "Deci, Koestner & Ryan, 1999", desc: "Three-stage motivation transition: extrinsic hook → competence shift → identity lock. Mastered habits become \"Hero Powers\" — part of who they are, not what they do for points." },
      { title: "Variable Reinforcement", source: "Skinner, 1957", desc: "Critical Success moments (1 in 8 chance) deliver bonus XP with spectacular animations. The unpredictability makes every habit completion exciting." },
      { title: "Critical Window: Age 3–9", source: "Pressman et al., Brown University", desc: "Habit patterns become fixed by age 9. This makes ages 3–9 the highest-impact intervention window. Nokuhiro is designed to maximize this critical period." },
    ],
    antiAddictionTitle: "Built to Help, Not to Hook",
    antiAddictionDesc: "The app naturally concludes after daily habits are completed — no endless scrolling or addictive loops. No streak anxiety — missing a day simply resets the counter without penalty. No loss mechanics — children never lose progress. No dark patterns, no FOMO, no social comparison. Typical healthy session: an estimated 2–5 minutes.",
  },
  pricing: {
    label: "Simple & Fair",
    heading: "Pay Once. Keep Forever.",
    subtitle: "One small purchase. No subscriptions. No ads. No in-app purchases. The full adventure is yours — forever.",
    badge: "Full App — Pay Once",
    alsoAvailable: (code) => code === "CHF" ? "Also available for 5 EUR / 5 USD" : code === "EUR" ? "Also available for 5 CHF / 5 USD" : "Also available for 5 CHF / 5 EUR",
    oneTime: "One-time purchase — yours forever",
    items: ["Unlimited habits", "Multiple child profiles", "All 11 companion animals", "Full quest map system", "Adaptive AI engine", "Three age-cohort modes", "Parent analytics dashboard", "5 languages included", "Offline mode", "No ads, ever", "No subscriptions", "No in-app purchases"],
    cta: "Download Now",
    whyTitle: "Why so affordable?",
    whyDesc: "We believe every child deserves great habits. One small payment unlocks everything — no paywalls, no premium tiers, no recurring fees.",
  },
  downloadSection: {
    heading1: "Ready to Build",
    heading2: " Lifelong Habits?",
    desc: "Nokuhiro is available now on iOS and Android. Download today and start the adventure for your family.",
    languages: "Available in <strong>English</strong>, <strong>German</strong>, <strong>Spanish</strong>, <strong>Italian</strong>, and <strong>French</strong>",
  },
  privacy: {
    label: "Your Family's Safety",
    heading: "Privacy Policy",
    sections: [
      { title: "Minimal Data Collection", content: "Nokuhiro collects only the data necessary to provide the service. All child profiles, habit data, progress, and companion information are stored securely on our servers with encryption in transit and at rest. We never share data with third parties or use it for advertising." },
      { title: "Zero Tracking & No Ads", content: "We use zero third-party analytics, advertising SDKs, or tracking tools. There are no ads in the app. No data is shared with advertisers, data brokers, or any third parties. We do not request the App Tracking Transparency prompt because there is nothing to track." },
      { title: "COPPA, GDPR-K & Swiss nFADP Compliant", content: "Nokuhiro is fully compliant with COPPA, GDPR-K, and the Swiss Federal Act on Data Protection (nFADP/DSG). Verifiable parental consent is required before any child profile is created. Children cannot create accounts, modify privacy settings, or access parental controls independently." },
      { title: "AI-Powered Features", content: "AI-powered features generate personalized progress summaries and habit recommendations for parents. The AI service processes data only to generate responses and does not store or retain any data afterward. AI features can be disabled at any time in settings." },
      { title: "Full Parental Control", content: "Parents can review, export, or delete all child data at any time directly within the App. You can request account deletion at any time, after which all server-side data is permanently deleted within 30 days." },
    ],
    contactText: "For privacy inquiries, data deletion requests, or any concerns, please contact us at",
  },
  footer: {
    desc: "Science-backed habit building for children aged 3–12. Transform daily routines into heroic adventures.",
    product: "Product",
    theScience: "The Science",
    scienceLinks: [
      { label: "Behavioral Research", href: "#science" },
      { label: "Growth Mindset", href: "#science" },
      { label: "Privacy & Safety", href: "/privacy" },
      { label: "Anti-Addiction Design", href: "#science" },
    ],
    support: "Support",
    supportLinks: [
      { label: "Contact Us", href: "mailto:hello@nokuhiro.com" },
      { label: "FAQ", href: "/faq" },
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
    ],
    followUs: "Follow Us",
    instagram: "Instagram",
    copyright: "Nokuhiro. All rights reserved.",
    coppa: "COPPA 2026 Compliant",
    gdpr: "GDPR-K Compliant",
  },
  heroCro: {
    heading: "Less nagging. More peaceful routines.",
    subheading: "Nokuhiro turns brushing teeth, homework, tidying up, bedtime and daily habits into playful quests, so children build independence and parents repeat themselves less.",
    ctaIos: "Download on the App Store",
    ctaAndroid: "Get it on Google Play",
    trustLine: "One-time purchase. No ads. No subscriptions. Designed for children aged 3\u201312.",
  },
  trustBar: {
    ages: "Ages 3\u201312",
    oneTime: "One-time purchase",
    noAds: "No ads or subscriptions",
    quickRoutines: "Quick routine check-ins",
  },
  howItWorksCro: {
    label: "HOW IT WORKS",
    heading: "From routine to adventure in 3 simple steps.",
    steps: [
      { icon: "1", title: "Create the routine", desc: "Parents choose habits such as brushing teeth, getting dressed, reading, tidying up, homework or kindness acts." },
      { icon: "2", title: "Your child follows the quest", desc: "Children complete visual steps with their companion and earn progress through the quest map." },
      { icon: "3", title: "Celebrate real-life progress", desc: "Badges, XP and family rewards help children feel proud of completing real habits." },
    ],
  },
  productClarity: {
    label: "SEE IT IN ACTION",
    heading: "See exactly what your child sees.",
    subtitle: "Real app screens showing how Nokuhiro guides your child through daily routines.",
    features: [
      { title: "Choose age-appropriate habits", desc: "Set up routines tailored to your child\u2019s age and needs." },
      { title: "Guide routines with visual steps", desc: "Children follow clear, illustrated quest steps." },
      { title: "Let children complete quests independently", desc: "Your child progresses through routines on their own." },
      { title: "Track progress without constant reminders", desc: "See how habits develop over time." },
      { title: "Celebrate consistency as a family", desc: "Reward real-world achievements together." },
    ],
  },
  parentPain: {
    label: "THE PROBLEM",
    heading: "Daily routines should not feel like a battle.",
    beforeTitle: "Without Nokuhiro",
    afterTitle: "With Nokuhiro",
    before: [
      "Repeating the same thing again and again",
      "Children losing focus",
      "Rushed mornings",
      "Bedtime negotiations",
      "Parents feeling frustrated",
    ],
    after: [
      "Clear visual steps",
      "More child independence",
      "Fewer reminders",
      "Calmer transitions",
      "Progress children can feel proud of",
    ],
  },
  childMotivation: {
    label: "FOR KIDS",
    heading: "Why kids want to come back.",
    subtitle: "Nokuhiro uses playful motivation to make real-life habits feel visible, achievable and rewarding. Children are guided by companions, quest maps, XP, badges and small wins \u2014 without turning routines into endless screen time.",
    items: [
      { icon: "\uD83E\uDD81", title: "Animal companions", desc: "A friendly guide that grows with your child." },
      { icon: "\uD83D\uDDFA\uFE0F", title: "Quest map", desc: "Visual progress through each routine." },
      { icon: "\u2B50", title: "XP and badges", desc: "Earn rewards for completing real habits." },
      { icon: "\uD83D\uDCC8", title: "Visual progress", desc: "Children see how far they have come." },
      { icon: "\uD83C\uDF89", title: "Celebrations", desc: "Moments of pride after completing quests." },
      { icon: "\uD83C\uDFC6", title: "Family rewards", desc: "Parents set real-world rewards for consistency." },
    ],
  },
  parentValue: {
    label: "FOR PARENTS",
    heading: "What parents get back.",
    benefits: [
      "Less repeating",
      "Fewer routine battles",
      "More independence",
      "Clearer mornings",
      "Calmer bedtime",
      "Better habit consistency",
      "One system for multiple routines",
    ],
    quote: "Morning routines used to be the most stressful part of our day. I felt like I was repeating the same instructions over and over. With Nokuhiro, our child can actually see what comes next and complete the steps more independently. It has made our mornings calmer and much less emotional.",
    quoteAttribution: "Parent of a 5-year-old",
    quote2: "We tried charts and reminders before, but they usually lost their effect after a few days. Nokuhiro feels different because the routine becomes something our child wants to follow. The quests, companion and progress make daily habits feel more positive \u2014 and we spend less time negotiating.",
    quote2Attribution: "Parent of two children, ages 4 and 7",
  },
  paidValue: {
    label: "WHY PAID",
    heading: "One purchase. No subscription. No ads.",
    body: "Nokuhiro is built as a focused family tool, not an attention trap. You pay once and get a safe, calm routine experience without ads, in-app purchases or subscription pressure.",
    points: [
      "Costs less than many family treats or one small toy",
      "Helps with routines parents face every day",
      "No monthly subscription",
      "Designed for repeated daily use",
      "Built for real-world habits, not passive entertainment",
    ],
    cta: "Download Nokuhiro today",
  },
  scienceCro: {
    label: "THE APPROACH",
    heading: "Built on how children actually learn.",
    body: "Nokuhiro combines visual routines, positive reinforcement, and playful motivation \u2014 approaches supported by child development research. Short, purposeful interactions help children build real habits without creating screen-time dependency.",
  },
  privacySafety: {
    label: "PRIVACY & SAFETY",
    heading: "Built for families. Designed with privacy in mind.",
    items: [
      { icon: "\uD83D\uDEAB", title: "No advertising", desc: "Your family will never see ads in Nokuhiro." },
      { icon: "\uD83D\uDC64", title: "No social features for children", desc: "No chat, no friends lists, no public profiles." },
      { icon: "\uD83D\uDD12", title: "No advertising trackers", desc: "No third-party ad networks track your child." },
      { icon: "\uD83D\uDCE6", title: "No selling of family data", desc: "Your family\u2019s data is never sold." },
      { icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67", title: "Parent-managed experience", desc: "Parents control routines, rewards and settings." },
      { icon: "\u23F1\uFE0F", title: "Designed for short, purposeful use", desc: "Quick routine check-ins, not endless screen time." },
    ],
  },
  founder: {
    label: "OUR STORY",
    heading: "Created by parents who needed this too.",
    body: "Nokuhiro was created by parents who knew the stress of repeating the same instructions every day. What started as a simple family system became an app designed to make routines calmer, clearer and more enjoyable.",
  },
  useCases: {
    label: "USE CASES",
    heading: "Works for the routines parents repeat every day.",
    items: [
      { title: "Morning routine", from: "Constant reminders to get ready", to: "A clear quest path your child follows step by step" },
      { title: "Brushing teeth", from: "Arguing about brushing properly", to: "A guided quest with visual steps" },
      { title: "Getting dressed", from: "Distracted and slow mornings", to: "Visual steps that keep children on track" },
      { title: "Homework", from: "Resistance and procrastination", to: "A structured quest with progress rewards" },
      { title: "Reading", from: "Difficulty building the habit", to: "A daily quest that celebrates consistency" },
      { title: "Tidying up", from: "Repeated requests to clean up", to: "A clear routine children can follow alone" },
      { title: "Bedtime routine", from: "Five more minutes negotiations", to: "A calming quest path to follow step by step" },
      { title: "Chores", from: "Ignoring requests to help around the house", to: "A quest that makes helping feel like an achievement" },
      { title: "Kindness acts", from: "Struggling to build empathy habits", to: "A daily quest that celebrates small acts of kindness" },
    ],
  },
  proof: {
    label: "REAL FAMILIES",
    heading: "Built around real family routine challenges.",
    quote1: "Morning routines used to be the most stressful part of our day. I felt like I was repeating the same instructions over and over. With Nokuhiro, our child can actually see what comes next and complete the steps more independently. It has made our mornings calmer and much less emotional.",
    quote1Attribution: "Parent of a 5-year-old",
    quote2: "We tried charts and reminders before, but they usually lost their effect after a few days. Nokuhiro feels different because the routine becomes something our child wants to follow. The quests, companion and progress make daily habits feel more positive \u2014 and we spend less time negotiating.",
    quote2Attribution: "Parent of two children, ages 4 and 7",
  },
  faqCro: {
    label: "FAQ",
    heading: "Common questions from parents.",
    items: [
      { q: "Is this just more screen time?", a: "No. Nokuhiro is designed for short, purposeful routine check-ins that help children complete real-world habits." },
      { q: "Is Nokuhiro a subscription?", a: "No. Nokuhiro is a one-time purchase." },
      { q: "What ages is it for?", a: "Nokuhiro is designed for children aged 3\u201312." },
      { q: "Does it work for morning and bedtime routines?", a: "Yes. Parents can create routines for mornings, bedtime, chores, homework, brushing teeth, tidying up, reading and more." },
      { q: "Can multiple children use it?", a: "Yes. You can create separate profiles for each child in your family." },
      { q: "Does it work offline?", a: "The core routine features work offline. An internet connection is needed for syncing progress across devices." },
      { q: "Does Nokuhiro use ads?", a: "No. Nokuhiro is designed without ads." },
      { q: "Does it track my child for advertising?", a: "No. Nokuhiro does not use advertising trackers." },
      { q: "Why is it paid?", a: "Because Nokuhiro is built as a focused family tool without ads, subscription pressure or attention-trap monetization." },
    ],
  },
  finalCta: {
    heading: "Ready for calmer routines?",
    ctaIos: "Download on the App Store",
    ctaAndroid: "Get it on Google Play",
  },
  sticky: {
    label: "Make routines calmer",
    ctaIos: "App Store",
    ctaAndroid: "Google Play",
  },
};

const de: Translations = {
  nav: {
    features: "Funktionen",
    howItWorks: "So funktioniert's",
    companions: "Begleiter",
    science: "Wissenschaft",
    pricing: "Preis",
    download: "Jetzt herunterladen",
    downloadNow: "Jetzt herunterladen",
  },
  betaSignup: {
    heading1: "Tritt der ",
    heading2: "geschlossenen Beta bei",
    desc: "Nokuhiro befindet sich derzeit in der geschlossenen Beta-Testphase. Melde dich an, um zu den ersten Familien zu gehören, die das Abenteuer erleben.",
    emailPlaceholder: "Deine E-Mail-Adresse",
    platformLabel: "Welche Plattform?",
    ios: "iOS",
    android: "Android",
    both: "Beide",
    submitBtn: "Beta beitreten",
    successMsg: "Du bist dabei! Wir benachrichtigen dich, wenn Nokuhiro zum Download bereit ist.",
    alreadyRegistered: "Du stehst bereits auf der Liste! Wir melden uns bald.",
    errorMsg: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
  },
  hero: {
    tagline: "",
    heading1: "Weniger Nörgeln.",
    heading2: "Mehr Wachsen.",
    subtitle: "Die Nr. 1 Gewohnheits-App für Kinder von 3–12 Jahren. Wissenschaftlich fundierte Routinen, die Kinder lieben — keine Werbung, kein Abo, nur ein Kauf.",
    cta1: "Jetzt herunterladen",
    cta2: "Funktionen entdecken",
    badge1: "Nur 5 {currency} — Keine Werbung",
    badge2: "Einmaliger Kauf",
    badge3: "COPPA & DSGVO-K sicher",
    badge4: "5 Sprachen",
  },
  preview: {
    label: "Triff Nokuhiro",
    heading1: "Die heldenhafte Reise",
    heading2: " deines Kindes beginnt hier",
    desc: "Geführt von Nokuhiro, dem Löwenhelden, erstellen Kinder ihren eigenen Helden-Avatar, wählen ein Begleittier aus 11 einzigartigen Charakteren und begeben sich auf tägliche Quests. Jede abgeschlossene Gewohnheit bringt XP, lässt ihren Helden aufsteigen und bringt sie näher an reale Belohnungen, die von den Eltern festgelegt werden.",
    feature1Title: "Nokuhiro — Dein Begleiter",
    feature1Desc: "Ein mutiger Löwenheld, der Ratschläge gibt, ermutigt und jeden Erfolg feiert",
    feature2Title: "Quest-Karten-Abenteuer",
    feature2Desc: "Gewundener goldener Pfad mit Meilensteinen, Begleitern und Schatzbelohnungen",
    feature3Title: "Adaptive KI-Engine",
    feature3Desc: "Passt XP dynamisch an Meisterschaft und Wachstum an — auf dem Gerät, privat",
  },
  features: {
    label: "Leistungsstarke Funktionen",
    heading: "Alles, was ein Held braucht",
    subtitle: "Basierend auf begutachteter Verhaltensforschung, entwickelt für Kinder, vertraut von Eltern.",
    items: [
      { title: "Quest-basierte Gewohnheiten", desc: "Tägliche Gewohnheiten werden zu heroischen Quests auf einem gewundenen goldenen Pfad. Kinder sehen ihren Fortschritt als echtes Abenteuer mit regelmäßigen Meilensteinen an wichtigen Fortschrittspunkten." },
      { title: "Lebendige Begleiter", desc: "Wähle aus 11 einzigartigen Tierbegleitern, die wachsen, reagieren und sich basierend auf den Gewohnheiten deines Kindes entwickeln. Sie sind für ihren Freund da — nicht nur für Punkte." },
      { title: "Adaptive KI-Engine", desc: "Die geräteinterne KI passt XP-Werte dynamisch an — belohnt Anstrengung bei vernachlässigten Gewohnheiten und reduziert Belohnungen bei gemeisterten, um echte intrinsische Motivation aufzubauen." },
      { title: "Geschwister-Team-Quests", desc: "Geschwister bündeln XP für gemeinsame Familienbelohnungen. Bosskämpfe, kooperative Herausforderungen und gemeinsame Feiern — kein toxischer Wettbewerb, nur Teamarbeit." },
      { title: "Drei Altersmodi", desc: "Entdecker (3–5) nutzt große bunte Symbole und einfache Interaktionen. Abenteurer (6–8) fügt volle Gamification hinzu. Champion (9–12) führt Statistiken und Autonomie ein. Eine App, drei Erlebnisse." },
      { title: "Datenschutz zuerst", desc: "Server-First-Architektur mit lokalem Cache. Kein Tracking durch Dritte. COPPA 2026 und DSGVO-K konform. Alle Daten verschlüsselt bei Übertragung und Speicherung." },
    ],
  },
  howItWorks: {
    label: "So funktioniert's",
    heading: "Vom Download zum täglichen Helden",
    subtitle: "Der Einstieg dauert weniger als zwei Minuten. Hier ist die Reise.",
    steps: [
      { title: "Eltern richten in 90 Sekunden ein", desc: "Füge den Namen und das Alter deines Kindes hinzu. Wähle aus intelligenten Gewohnheitsvorschlägen mit Mikro-Gewohnheits-Standards. Lege eine reale Belohnung und Quest-Dauer fest." },
      { title: "Kind trifft Nokuhiro", desc: "Nokuhiro, der Löwenheld, begrüsst dein Kind im verzauberten Wald. Es wählt sein Begleittier und schliesst seine erste Quest ab — mit einer grossen Feier." },
      { title: "Tägliche Quests bilden Gewohnheiten", desc: "Jeden Tag schliessen Kinder Gewohnheits-Quests ab, um XP zu verdienen, ihren Begleiter aufzuleveln und auf dem goldenen Quest-Pfad voranzukommen." },
      { title: "Fortschritt gemeinsam verfolgen", desc: "Eltern überwachen den Fortschritt über das Dashboard mit Einblicken, Wochenberichten und Abschlussstatistiken. Feiert Meilensteine als Familie." },
    ],
  },
  companions: {
    label: "Wähle deinen Begleiter",
    heading: "11 einzigartige Abenteuer-Freunde",
    subtitle: "Jeder Begleiter hat seine eigene Persönlichkeit, Stimme und besondere Fähigkeiten. Sie entwickeln sich durch 5 Stufen — vom Schlüpfling zur Legende.",
    evolutionTitle: "5 Entwicklungsstufen",
    stages: ["Schlüpfling", "Entdecker", "Abenteurer", "Champion", "Legende"],
    animals: ["Dinosaurier", "Einhorn", "Hase", "Fuchs", "Eule", "Affe", "Elefant", "Löwe", "Panda", "Bär", "Kuh"],
    styles: ["Mutig & Begeistert", "Magisch & Verträumt", "Energisch & Schnell", "Clever & Strategisch", "Weise & Nachdenklich", "Abenteuerlustig & Lustig", "Sanft & Weise", "Tapfer & Stolz", "Verspielt & Sanft", "Stark & Entschlossen", "Ruhig & Fürsorglich"],
    traits: ["Gräbt Bonusschätze aus", "Hinterlässt Regenbogenspur", "Rennt den Quest-Pfad entlang", "Findet versteckte Abkürzungen", "Teilt Wissenswertes nach Gewohnheiten", "Hat einen Giraffen-Kumpel", "Vergisst nie einen Streak", "Mähne leuchtet bei Streaks", "Macht Purzelbaum-Feiern", "Zeigt Stärke-Animationen", "Lässt einen Blumengarten wachsen"],
  },
  science: {
    label: "Wissenschaftlich fundiert",
    heading: "Wissenschaft, keine Spielerei",
    subtitle: "Jede Funktion basiert auf begutachteter Verhaltensforschung. Dies ist keine Aufgabenliste mit Stickern — es ist ein präzise entwickeltes Verhaltenssystem, getarnt als Abenteuerspiel.",
    items: [
      { title: "66-Tage-Automatisierung", source: "Lally et al., 2010", desc: "Forschung zeigt, dass Gewohnheiten im Median 66 Tage brauchen, um automatisch zu werden. Unsere Quest-Zyklen sind auf 28–42 Tage kalibriert mit regelmäßigen Meilensteinen entlang der Reise." },
      { title: "Tiny Habits Methode", source: "BJ Fogg, Stanford", desc: "Jede Gewohnheit beginnt mikro — unter 30 Sekunden. Nach 7 aufeinanderfolgenden Tagen schlägt die App eine Erweiterung vor. Klein anfangen, Vertrauen aufbauen, mit Elternzustimmung skalieren." },
      { title: "Growth Mindset Feedback", source: "Carol Dweck, Stanford", desc: "Alles Lob fokussiert auf Anstrengung, nie auf Fähigkeit. «Du hast so hart gearbeitet!» baut Resilienz auf. «Du bist so schlau!» erzeugt Fragilität. Jeder Text folgt diesem Prinzip." },
      { title: "Überbegründungs-Prävention", source: "Deci, Koestner & Ryan, 1999", desc: "Dreistufiger Motivationsübergang: extrinsischer Anreiz → Kompetenzverschiebung → Identitätsverankerung. Gemeisterte Gewohnheiten werden zu «Heldenkräften» — Teil dessen, wer sie sind." },
      { title: "Variable Verstärkung", source: "Skinner, 1957", desc: "Kritische Erfolgsmomente (1 von 8 Chance) liefern Bonus-XP mit spektakulären Animationen. Die Unvorhersehbarkeit macht jede Gewohnheitserfüllung aufregend." },
      { title: "Kritisches Fenster: 3–9 Jahre", source: "Pressman et al., Brown University", desc: "Gewohnheitsmuster verfestigen sich bis zum Alter von 9 Jahren. Das macht 3–9 Jahre zum wirkungsvollsten Interventionsfenster. Nokuhiro ist für diese kritische Phase konzipiert." },
    ],
    antiAddictionTitle: "Gebaut zum Helfen, nicht zum Fesseln",
    antiAddictionDesc: "Die App endet natürlich nach Abschluss der täglichen Gewohnheiten — kein endloses Scrollen oder süchtig machende Schleifen. Keine Streak-Angst — ein verpasster Tag setzt den Zähler einfach ohne Strafe zurück. Keine Verlustmechaniken — Kinder verlieren nie Fortschritt. Keine Dark Patterns, kein FOMO, kein sozialer Vergleich. Typische gesunde Sitzung: geschätzt 2–5 Minuten.",
  },
  pricing: {
    label: "Einfach & Fair",
    heading: "Einmal zahlen. Für immer behalten.",
    subtitle: "Ein kleiner Kauf. Keine Abonnements. Keine Werbung. Keine In-App-Käufe. Das volle Abenteuer gehört dir — für immer.",
    badge: "Volle App — Einmalzahlung",
    alsoAvailable: (code) => code === "CHF" ? "Auch verfügbar für 5 EUR / 5 USD" : code === "EUR" ? "Auch verfügbar für 5 CHF / 5 USD" : "Auch verfügbar für 5 CHF / 5 EUR",
    oneTime: "Einmaliger Kauf — für immer deins",
    items: ["Unbegrenzte Gewohnheiten", "Mehrere Kinderprofile", "Alle 11 Begleittiere", "Volles Quest-Karten-System", "Adaptive KI-Engine", "Drei Altersgruppen-Modi", "Eltern-Analyse-Dashboard", "5 Sprachen inklusive", "Offline-Modus", "Keine Werbung, nie", "Keine Abonnements", "Keine In-App-Käufe"],
    cta: "Jetzt herunterladen",
    whyTitle: "Warum so günstig?",
    whyDesc: "Wir glauben, dass jedes Kind gute Gewohnheiten verdient. Eine kleine Zahlung schaltet alles frei — keine Bezahlschranken, keine Premium-Stufen, keine wiederkehrenden Gebühren.",
  },
  downloadSection: {
    heading1: "Bereit, lebenslange",
    heading2: " Gewohnheiten aufzubauen?",
    desc: "Nokuhiro ist jetzt im App Store und bei Google Play verfügbar. Lade die App herunter und starte das Abenteuer für deine Familie.",
    languages: "Verfügbar auf <strong>Englisch</strong>, <strong>Deutsch</strong>, <strong>Spanisch</strong>, <strong>Italienisch</strong> und <strong>Französisch</strong>",
  },
  privacy: {
    label: "Die Sicherheit deiner Familie",
    heading: "Datenschutzrichtlinie",
    sections: [
      { title: "Minimale Datenerhebung", content: "Nokuhiro erhebt nur die Daten, die für den Dienst notwendig sind. Alle Kinderprofile, Gewohnheitsdaten, Fortschritte und Begleiter-Informationen werden sicher auf unseren Servern gespeichert, verschlüsselt bei Übertragung und Speicherung. Wir teilen niemals Daten mit Dritten oder nutzen sie für Werbung." },
      { title: "Kein Tracking & keine Werbung", content: "Wir verwenden keine Drittanbieter-Analysen, Werbe-SDKs oder Tracking-Tools. Es gibt keine Werbung in der App. Keine Daten werden mit Werbetreibenden, Datenhändlern oder Dritten geteilt. Wir fordern keine App-Tracking-Transparenz an, weil es nichts zu verfolgen gibt." },
      { title: "COPPA, DSGVO-K & Schweizer DSG konform", content: "Nokuhiro ist vollständig konform mit COPPA, DSGVO-K und dem Schweizer Datenschutzgesetz (nDSG). Überprüfbare elterliche Zustimmung ist erforderlich, bevor ein Kinderprofil erstellt wird. Kinder können keine Konten erstellen, Datenschutzeinstellungen ändern oder auf elterliche Kontrollen zugreifen." },
      { title: "KI-gestützte Funktionen", content: "KI-gestützte Funktionen generieren personalisierte Fortschrittsberichte und Gewohnheitsempfehlungen für Eltern. Der KI-Dienst verarbeitet Daten nur zur Antwortgenerierung und speichert keine Daten danach. KI-Funktionen können jederzeit in den Einstellungen deaktiviert werden." },
      { title: "Volle elterliche Kontrolle", content: "Eltern können alle Kinderdaten jederzeit direkt in der App überprüfen, exportieren oder löschen. Du kannst jederzeit die Kontolöschung beantragen, woraufhin alle serverseitigen Daten innerhalb von 30 Tagen dauerhaft gelöscht werden." },
    ],
    contactText: "Für Datenschutzanfragen, Löschanträge oder Bedenken kontaktiere uns bitte unter",
  },
  footer: {
    desc: "Wissenschaftlich fundierte Gewohnheitsbildung für Kinder von 3–12 Jahren. Verwandle tägliche Routinen in heldenhafte Abenteuer.",
    product: "Produkt",
    theScience: "Die Wissenschaft",
    scienceLinks: [
      { label: "Verhaltensforschung", href: "#science" },
      { label: "Growth Mindset", href: "#science" },
      { label: "Datenschutz & Sicherheit", href: "/privacy" },
      { label: "Anti-Sucht-Design", href: "#science" },
    ],
    support: "Support",
    supportLinks: [
      { label: "Kontakt", href: "mailto:hello@nokuhiro.com" },
      { label: "FAQ", href: "/faq" },
      { label: "Datenschutzrichtlinie", href: "/privacy" },
      { label: "Nutzungsbedingungen", href: "/terms" },
    ],
    followUs: "Folge uns",
    instagram: "Instagram",
    copyright: "Nokuhiro. Alle Rechte vorbehalten.",
    coppa: "COPPA 2026 konform",
    gdpr: "DSGVO-K konform",
  },
  heroCro: {
    heading: "Weniger Nörgeln. Ruhigere Routinen.",
    subheading: "Nokuhiro verwandelt Zähneputzen, Hausaufgaben, Aufräumen, Schlafenszeit und tägliche Gewohnheiten in spielerische Quests, damit Kinder selbstständiger werden und Eltern sich weniger wiederholen müssen.",
    ctaIos: "Im App Store laden",
    ctaAndroid: "Bei Google Play laden",
    trustLine: "Einmalkauf. Keine Werbung. Kein Abo. Für Kinder von 3\u201312 Jahren.",
  },
  trustBar: {
    ages: "Alter 3\u201312",
    oneTime: "Einmalkauf",
    noAds: "Keine Werbung oder Abos",
    quickRoutines: "Kurze Routine-Check-ins",
  },
  howItWorksCro: {
    label: "SO FUNKTIONIERT\u2019S",
    heading: "Von der Routine zum Abenteuer in 3 einfachen Schritten.",
    steps: [
      { icon: "1", title: "Routine erstellen", desc: "Eltern wählen Gewohnheiten wie Zähneputzen, Anziehen, Lesen, Aufräumen, Hausaufgaben oder freundliche Taten." },
      { icon: "2", title: "Dein Kind folgt dem Quest", desc: "Kinder erledigen visuelle Schritte mit ihrem Begleiter und verdienen Fortschritt auf der Quest-Karte." },
      { icon: "3", title: "Echten Fortschritt feiern", desc: "Abzeichen, XP und Familienbelohnungen helfen Kindern, stolz auf echte Gewohnheiten zu sein." },
    ],
  },
  productClarity: {
    label: "IN AKTION SEHEN",
    heading: "Sieh genau, was dein Kind sieht.",
    subtitle: "Echte App-Bildschirme zeigen, wie Nokuhiro dein Kind durch tägliche Routinen führt.",
    features: [
      { title: "Altersgerechte Gewohnheiten wählen", desc: "Routinen passend zum Alter und den Bedürfnissen deines Kindes einrichten." },
      { title: "Routinen mit visuellen Schritten begleiten", desc: "Kinder folgen klaren, illustrierten Quest-Schritten." },
      { title: "Kinder erledigen Quests selbstständig", desc: "Dein Kind macht eigenständig Fortschritte bei Routinen." },
      { title: "Fortschritt ohne ständige Erinnerungen verfolgen", desc: "Sieh, wie sich Gewohnheiten über die Zeit entwickeln." },
      { title: "Beständigkeit als Familie feiern", desc: "Belohnt gemeinsam echte Erfolge." },
    ],
  },
  parentPain: {
    label: "DAS PROBLEM",
    heading: "Tägliche Routinen sollten kein Kampf sein.",
    beforeTitle: "Ohne Nokuhiro",
    afterTitle: "Mit Nokuhiro",
    before: [
      "Immer wieder das Gleiche wiederholen",
      "Kinder verlieren den Fokus",
      "Hektische Morgen",
      "Schlafenszeit-Verhandlungen",
      "Eltern fühlen sich frustriert",
    ],
    after: [
      "Klare visuelle Schritte",
      "Mehr Selbstständigkeit",
      "Weniger Erinnerungen",
      "Ruhigere Übergänge",
      "Fortschritt, auf den Kinder stolz sein können",
    ],
  },
  childMotivation: {
    label: "FÜR KINDER",
    heading: "Warum Kinder immer wieder zurückkommen.",
    subtitle: "Nokuhiro nutzt spielerische Motivation, damit echte Gewohnheiten sichtbar, erreichbar und lohnend werden. Kinder werden von Begleitern, Quest-Karten, XP, Abzeichen und kleinen Erfolgen geführt \u2014 ohne Routinen in endlose Bildschirmzeit zu verwandeln.",
    items: [
      { icon: "\uD83E\uDD81", title: "Tierische Begleiter", desc: "Ein freundlicher Begleiter, der mit deinem Kind wächst." },
      { icon: "\uD83D\uDDFA\uFE0F", title: "Quest-Karte", desc: "Visueller Fortschritt durch jede Routine." },
      { icon: "\u2B50", title: "XP und Abzeichen", desc: "Belohnungen für echte Gewohnheiten verdienen." },
      { icon: "\uD83D\uDCC8", title: "Visueller Fortschritt", desc: "Kinder sehen, wie weit sie gekommen sind." },
      { icon: "\uD83C\uDF89", title: "Feiern", desc: "Momente des Stolzes nach abgeschlossenen Quests." },
      { icon: "\uD83C\uDFC6", title: "Familienbelohnungen", desc: "Eltern setzen echte Belohnungen für Beständigkeit." },
    ],
  },
  parentValue: {
    label: "FÜR ELTERN",
    heading: "Was Eltern zurückbekommen.",
    benefits: [
      "Weniger Wiederholen",
      "Weniger Routine-Kämpfe",
      "Mehr Selbstständigkeit",
      "Klarere Morgen",
      "Ruhigere Schlafenszeit",
      "Bessere Gewohnheits-Beständigkeit",
      "Ein System für mehrere Routinen",
    ],
    quote: "Morgenroutinen waren der stressigste Teil unseres Tages. Ich hatte das Gefühl, immer wieder die gleichen Anweisungen zu wiederholen. Mit Nokuhiro kann unser Kind sehen, was als Nächstes kommt, und die Schritte selbstständiger erledigen. Es hat unsere Morgen ruhiger und weniger emotional gemacht.",
    quoteAttribution: "Elternteil eines 5-Jährigen",
    quote2: "Wir haben vorher Tabellen und Erinnerungen ausprobiert, aber sie verloren meist nach ein paar Tagen ihre Wirkung. Nokuhiro fühlt sich anders an, weil die Routine etwas wird, dem unser Kind folgen möchte. Die Quests, der Begleiter und der Fortschritt machen tägliche Gewohnheiten positiver \u2014 und wir verbringen weniger Zeit mit Verhandeln.",
    quote2Attribution: "Elternteil von zwei Kindern, 4 und 7 Jahre alt",
  },
  paidValue: {
    label: "WARUM KOSTENPFLICHTIG",
    heading: "Einmalkauf. Kein Abo. Keine Werbung.",
    body: "Nokuhiro ist als fokussiertes Familien-Tool gebaut, nicht als Aufmerksamkeitsfalle. Du zahlst einmal und bekommst eine sichere, ruhige Routine-Erfahrung ohne Werbung, In-App-Käufe oder Abo-Druck.",
    points: [
      "Kostet weniger als viele Familienausflüge oder ein kleines Spielzeug",
      "Hilft bei Routinen, die Eltern jeden Tag bewältigen",
      "Kein monatliches Abo",
      "Für tägliche Nutzung konzipiert",
      "Für echte Gewohnheiten gebaut, nicht für passive Unterhaltung",
    ],
    cta: "Nokuhiro heute herunterladen",
  },
  scienceCro: {
    label: "DER ANSATZ",
    heading: "Basiert darauf, wie Kinder wirklich lernen.",
    body: "Nokuhiro kombiniert visuelle Routinen, positive Verstärkung und spielerische Motivation \u2014 Ansätze, die durch Forschung zur Kindesentwicklung unterstützt werden. Kurze, zielgerichtete Interaktionen helfen Kindern, echte Gewohnheiten aufzubauen, ohne Bildschirmzeit-Abhängigkeit zu schaffen.",
  },
  privacySafety: {
    label: "DATENSCHUTZ & SICHERHEIT",
    heading: "Für Familien gebaut. Mit Datenschutz im Blick.",
    items: [
      { icon: "\uD83D\uDEAB", title: "Keine Werbung", desc: "Deine Familie wird in Nokuhiro nie Werbung sehen." },
      { icon: "\uD83D\uDC64", title: "Keine sozialen Funktionen für Kinder", desc: "Kein Chat, keine Freundeslisten, keine öffentlichen Profile." },
      { icon: "\uD83D\uDD12", title: "Keine Werbe-Tracker", desc: "Keine Werbenetzwerke verfolgen dein Kind." },
      { icon: "\uD83D\uDCE6", title: "Kein Verkauf von Familiendaten", desc: "Die Daten deiner Familie werden nie verkauft." },
      { icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67", title: "Von Eltern verwaltet", desc: "Eltern kontrollieren Routinen, Belohnungen und Einstellungen." },
      { icon: "\u23F1\uFE0F", title: "Für kurze, zielgerichtete Nutzung", desc: "Schnelle Routine-Check-ins, keine endlose Bildschirmzeit." },
    ],
  },
  founder: {
    label: "UNSERE GESCHICHTE",
    heading: "Von Eltern geschaffen, die das auch brauchten.",
    body: "Nokuhiro wurde von Eltern geschaffen, die den Stress kannten, jeden Tag die gleichen Anweisungen zu wiederholen. Was als einfaches Familiensystem begann, wurde zu einer App, die Routinen ruhiger, klarer und angenehmer macht.",
  },
  useCases: {
    label: "ANWENDUNGSFÄLLE",
    heading: "Funktioniert für die Routinen, die Eltern jeden Tag wiederholen.",
    items: [
      { title: "Morgenroutine", from: "Ständige Erinnerungen, fertig zu werden", to: "Ein klarer Quest-Pfad, dem dein Kind Schritt für Schritt folgt" },
      { title: "Zähneputzen", from: "Streit über richtiges Putzen", to: "Ein geführter Quest mit visuellen Schritten" },
      { title: "Anziehen", from: "Abgelenkte und langsame Morgen", to: "Visuelle Schritte, die Kinder auf Kurs halten" },
      { title: "Hausaufgaben", from: "Widerstand und Aufschieben", to: "Ein strukturierter Quest mit Fortschrittsbelohnungen" },
      { title: "Lesen", from: "Schwierigkeiten, die Gewohnheit aufzubauen", to: "Ein täglicher Quest, der Beständigkeit feiert" },
      { title: "Aufräumen", from: "Wiederholte Bitten aufzuräumen", to: "Eine klare Routine, der Kinder allein folgen können" },
      { title: "Schlafenszeit", from: "Noch fünf Minuten-Verhandlungen", to: "Ein beruhigender Quest-Pfad zum Schritt-für-Schritt-Folgen" },
      { title: "Hausarbeit", from: "Bitten um Hilfe im Haushalt ignorieren", to: "Ein Quest, der Helfen wie eine Leistung fühlen lässt" },
      { title: "Freundliche Taten", from: "Schwierigkeiten, Empathie-Gewohnheiten aufzubauen", to: "Ein täglicher Quest, der kleine Akte der Freundlichkeit feiert" },
    ],
  },
  proof: {
    label: "ECHTE FAMILIEN",
    heading: "Gebaut f\u00fcr echte Familien-Routine-Herausforderungen.",
    quote1: "Morgenroutinen waren der stressigste Teil unseres Tages. Ich hatte das Gef\u00fchl, immer wieder die gleichen Anweisungen zu wiederholen. Mit Nokuhiro kann unser Kind sehen, was als N\u00e4chstes kommt, und die Schritte selbstst\u00e4ndiger erledigen. Es hat unsere Morgen ruhiger und weniger emotional gemacht.",
    quote1Attribution: "Elternteil eines 5-J\u00e4hrigen",
    quote2: "Wir haben vorher Tabellen und Erinnerungen ausprobiert, aber sie verloren meist nach ein paar Tagen ihre Wirkung. Nokuhiro f\u00fchlt sich anders an, weil die Routine etwas wird, dem unser Kind folgen m\u00f6chte. Die Quests, der Begleiter und der Fortschritt machen t\u00e4gliche Gewohnheiten positiver \u2014 und wir verbringen weniger Zeit mit Verhandeln.",
    quote2Attribution: "Elternteil von zwei Kindern, 4 und 7 Jahre alt",
  },
  faqCro: {
    label: "FAQ",
    heading: "Häufige Fragen von Eltern.",
    items: [
      { q: "Ist das nur mehr Bildschirmzeit?", a: "Nein. Nokuhiro ist für kurze, zielgerichtete Routine-Check-ins konzipiert, die Kindern helfen, echte Gewohnheiten zu erledigen." },
      { q: "Ist Nokuhiro ein Abo?", a: "Nein. Nokuhiro ist ein Einmalkauf." },
      { q: "Für welche Altersgruppe?", a: "Nokuhiro ist für Kinder von 3\u201312 Jahren konzipiert." },
      { q: "Funktioniert es für Morgen- und Schlafenszeit-Routinen?", a: "Ja. Eltern können Routinen für Morgen, Schlafenszeit, Hausarbeit, Hausaufgaben, Zähneputzen, Aufräumen, Lesen und mehr erstellen." },
      { q: "Können mehrere Kinder es nutzen?", a: "Ja. Du kannst separate Profile für jedes Kind in deiner Familie erstellen." },
      { q: "Funktioniert es offline?", a: "Die Kern-Routine-Funktionen funktionieren offline. Eine Internetverbindung wird für die Synchronisierung des Fortschritts benötigt." },
      { q: "Nutzt Nokuhiro Werbung?", a: "Nein. Nokuhiro ist ohne Werbung konzipiert." },
      { q: "Wird mein Kind für Werbung verfolgt?", a: "Nein. Nokuhiro nutzt keine Werbe-Tracker." },
      { q: "Warum ist es kostenpflichtig?", a: "Weil Nokuhiro als fokussiertes Familien-Tool ohne Werbung, Abo-Druck oder Aufmerksamkeitsfallen-Monetarisierung gebaut ist." },
    ],
  },
  finalCta: {
    heading: "Bereit für ruhigere Routinen?",
    ctaIos: "Im App Store laden",
    ctaAndroid: "Bei Google Play laden",
  },
  sticky: {
    label: "Routinen ruhiger machen",
    ctaIos: "App Store",
    ctaAndroid: "Google Play",
  },
};

const es: Translations = {
  nav: {
    features: "Funciones",
    howItWorks: "Cómo funciona",
    companions: "Compañeros",
    science: "Ciencia",
    pricing: "Precio",
    download: "Descargar ahora",
    downloadNow: "Descargar ahora",
  },
  betaSignup: {
    heading1: "Únete a la ",
    heading2: "Beta cerrada",
    desc: "Nokuhiro se encuentra actualmente en fase de prueba beta cerrada. Regístrate para ser de las primeras familias en vivir la aventura.",
    emailPlaceholder: "Tu dirección de correo",
    platformLabel: "¿Qué plataforma?",
    ios: "iOS",
    android: "Android",
    both: "Ambas",
    submitBtn: "Unirse a la Beta",
    successMsg: "¡Estás dentro! Te avisaremos cuando Nokuhiro esté listo para descargar.",
    alreadyRegistered: "¡Ya estás en la lista! Nos pondremos en contacto pronto.",
    errorMsg: "Algo salió mal. Por favor, inténtalo de nuevo.",
  },
  hero: {
    tagline: "",
    heading1: "Menos regaños.",
    heading2: "Más crecimiento.",
    subtitle: "La app de hábitos nº 1 para niños de 3 a 12 años. Rutinas respaldadas por la ciencia que los niños adoran — sin anuncios, sin suscripciones.",
    cta1: "Descargar ahora",
    cta2: "Explorar funciones",
    badge1: "Solo 5 {currency} — Sin anuncios",
    badge2: "Compra única",
    badge3: "COPPA y RGPD-K seguro",
    badge4: "5 idiomas",
  },
  preview: {
    label: "Conoce a Nokuhiro",
    heading1: "El viaje heroico",
    heading2: " de tu hijo comienza aquí",
    desc: "Guiados por Nokuhiro, el león héroe, los niños crean su propio avatar de héroe, eligen un animal compañero de entre 11 personajes únicos y se embarcan en misiones diarias. Cada hábito completado gana XP, sube de nivel a su héroe y les acerca a recompensas reales establecidas por los padres.",
    feature1Title: "Nokuhiro — Tu guía",
    feature1Desc: "Un valiente león héroe que ofrece consejos, ánimo y celebra cada logro",
    feature2Title: "Aventuras en el mapa",
    feature2Desc: "Camino dorado serpenteante con hitos, compañeros y recompensas de tesoro",
    feature3Title: "Motor de IA adaptativa",
    feature3Desc: "Ajusta dinámicamente los XP según el dominio y el crecimiento — en el dispositivo, privado",
  },
  features: {
    label: "Funciones potentes",
    heading: "Todo lo que un héroe necesita",
    subtitle: "Basado en ciencia del comportamiento revisada por pares, diseñado para niños, confiado por padres.",
    items: [
      { title: "Hábitos basados en misiones", desc: "Los hábitos diarios se convierten en misiones heroicas en un camino dorado serpenteante. Los niños ven su progreso como una aventura real con hitos regulares en puntos clave de progreso." },
      { title: "Compañeros vivos", desc: "Elige entre 11 compañeros animales únicos que crecen, reaccionan y evolucionan según los hábitos de tu hijo. Están ahí por su amigo — no solo por puntos." },
      { title: "Motor de IA adaptativa", desc: "La IA del dispositivo ajusta dinámicamente los valores de XP — recompensando el esfuerzo en hábitos descuidados y reduciendo recompensas en los dominados para construir motivación intrínseca real." },
      { title: "Misiones en equipo de hermanos", desc: "Los hermanos acumulan XP hacia recompensas familiares compartidas. Batallas de jefes, desafíos cooperativos y celebraciones conjuntas — sin competencia tóxica, solo trabajo en equipo." },
      { title: "Tres modos de edad", desc: "Explorador (3–5) usa iconos grandes y coloridos e interacciones simples. Aventurero (6–8) añade gamificación completa. Campeón (9–12) introduce estadísticas y autonomía. Una app, tres experiencias." },
      { title: "Diseño privacidad primero", desc: "Arquitectura server-first con caché local. Cero rastreo de terceros. Conforme con COPPA 2026 y RGPD-K. Todos los datos cifrados en tránsito y en reposo." },
    ],
  },
  howItWorks: {
    label: "Cómo funciona",
    heading: "De la descarga al héroe diario",
    subtitle: "Empezar lleva menos de dos minutos. Aquí está el viaje.",
    steps: [
      { title: "Los padres configuran en 90 segundos", desc: "Añade el nombre y la edad de tu hijo. Elige entre sugerencias inteligentes de hábitos con micro-hábitos predeterminados. Establece una recompensa real y la duración de la misión." },
      { title: "El niño conoce a Nokuhiro", desc: "Nokuhiro, el león héroe, da la bienvenida a tu hijo al bosque encantado. Eligen su animal compañero y completan su primera misión — con una gran celebración." },
      { title: "Las misiones diarias crean hábitos", desc: "Cada día, los niños completan misiones de hábitos para ganar XP, subir de nivel a su compañero y avanzar por el camino dorado hacia su recompensa." },
      { title: "Seguir el progreso juntos", desc: "Los padres monitorizan el progreso a través del panel con información, informes semanales y estadísticas de finalización. Celebrad los hitos en familia." },
    ],
  },
  companions: {
    label: "Elige tu compañero",
    heading: "11 compañeros de aventura únicos",
    subtitle: "Cada compañero tiene su propia personalidad, voz y habilidades especiales. Evolucionan a través de 5 etapas — de Cría a Leyenda.",
    evolutionTitle: "5 etapas de evolución",
    stages: ["Cría", "Explorador", "Aventurero", "Campeón", "Leyenda"],
    animals: ["Dinosaurio", "Unicornio", "Conejo", "Zorro", "Búho", "Mono", "Elefante", "León", "Panda", "Oso", "Vaca"],
    styles: ["Audaz y Entusiasta", "Mágico y Soñador", "Enérgico y Rápido", "Astuto y Estratégico", "Sabio y Reflexivo", "Aventurero y Divertido", "Gentil y Sabio", "Valiente y Orgulloso", "Juguetón y Gentil", "Fuerte y Determinado", "Tranquilo y Cariñoso"],
    traits: ["Desentierra tesoros extra", "Deja un rastro arcoíris", "Corre por el camino de la misión", "Encuentra atajos ocultos", "Comparte datos curiosos", "Tiene una jirafa compañera", "Nunca olvida una racha", "La melena brilla con las rachas", "Hace celebraciones con volteretas", "Muestra animaciones de fuerza", "Cultiva un jardín de flores"],
  },
  science: {
    label: "Respaldado por la investigación",
    heading: "Ciencia, no trucos",
    subtitle: "Cada función está basada en ciencia del comportamiento revisada por pares. Esto no es una lista de tareas con pegatinas — es un sistema conductual de precisión disfrazado de juego de aventuras.",
    items: [
      { title: "Automaticidad de 66 días", source: "Lally et al., 2010", desc: "La investigación muestra que los hábitos tardan una mediana de 66 días en volverse automáticos. Nuestros ciclos de misión están calibrados a 28–42 días con hitos regulares a lo largo del camino." },
      { title: "Marco de Tiny Habits", source: "BJ Fogg, Stanford", desc: "Cada hábito comienza micro — menos de 30 segundos. Después de 7 días consecutivos, la app sugiere expandir. Empieza pequeño, construye confianza, escala con aprobación de los padres." },
      { title: "Feedback de mentalidad de crecimiento", source: "Carol Dweck, Stanford", desc: "Todo elogio se centra en el esfuerzo, nunca en la habilidad. «¡Has trabajado muy duro!» construye resiliencia. «¡Eres muy listo!» crea fragilidad. Cada texto sigue este principio." },
      { title: "Prevención de sobrejustificación", source: "Deci, Koestner & Ryan, 1999", desc: "Transición motivacional en tres etapas: gancho extrínseco → cambio de competencia → anclaje de identidad. Los hábitos dominados se convierten en «Poderes de Héroe» — parte de quiénes son." },
      { title: "Refuerzo variable", source: "Skinner, 1957", desc: "Momentos de Éxito Crítico (1 de cada 8) entregan XP de bonificación con animaciones espectaculares. La imprevisibilidad hace que cada hábito completado sea emocionante." },
      { title: "Ventana crítica: 3–9 años", source: "Pressman et al., Brown University", desc: "Los patrones de hábitos se fijan a los 9 años. Esto hace que 3–9 años sea la ventana de intervención de mayor impacto. Nokuhiro está diseñado para maximizar este período crítico." },
    ],
    antiAddictionTitle: "Diseñado para ayudar, no para enganchar",
    antiAddictionDesc: "La app concluye naturalmente al completar los hábitos diarios — sin desplazamiento infinito ni bucles adictivos. Sin ansiedad por rachas — faltar un día simplemente reinicia el contador sin penalización. Sin mecánicas de pérdida — los niños nunca pierden progreso. Sin patrones oscuros, sin FOMO, sin comparación social. Sesión saludable típica: aproximadamente 2–5 minutos.",
  },
  pricing: {
    label: "Simple y justo",
    heading: "Paga una vez. Quédatelo para siempre.",
    subtitle: "Una pequeña compra. Sin suscripciones. Sin anuncios. Sin compras dentro de la app. La aventura completa es tuya — para siempre.",
    badge: "App completa — Pago único",
    alsoAvailable: (code) => code === "CHF" ? "También disponible por 5 EUR / 5 USD" : code === "EUR" ? "También disponible por 5 CHF / 5 USD" : "También disponible por 5 CHF / 5 EUR",
    oneTime: "Compra única — tuyo para siempre",
    items: ["Hábitos ilimitados", "Múltiples perfiles de niños", "Los 11 animales compañeros", "Sistema completo de mapa de misiones", "Motor de IA adaptativa", "Tres modos por edad", "Panel de análisis para padres", "5 idiomas incluidos", "Modo sin conexión", "Sin anuncios, nunca", "Sin suscripciones", "Sin compras en la app"],
    cta: "Descargar ahora",
    whyTitle: "¿Por qué tan asequible?",
    whyDesc: "Creemos que cada niño merece buenos hábitos. Un pequeño pago desbloquea todo — sin muros de pago, sin niveles premium, sin tarifas recurrentes.",
  },
  downloadSection: {
    heading1: "¿Listo para construir",
    heading2: " hábitos de por vida?",
    desc: "Nokuhiro ya está disponible en iOS y Android. Descárgalo hoy y comienza la aventura para tu familia.",
    languages: "Disponible en <strong>inglés</strong>, <strong>alemán</strong>, <strong>español</strong>, <strong>italiano</strong> y <strong>francés</strong>",
  },
  privacy: {
    label: "La seguridad de tu familia",
    heading: "Política de privacidad",
    sections: [
      { title: "Recopilación mínima de datos", content: "Nokuhiro recopila solo los datos necesarios para proporcionar el servicio. Todos los perfiles infantiles, datos de hábitos, progreso e información de compañeros se almacenan de forma segura en nuestros servidores, cifrados en tránsito y en reposo. Nunca compartimos datos con terceros ni los usamos para publicidad." },
      { title: "Cero rastreo y sin anuncios", content: "No utilizamos análisis de terceros, SDKs publicitarios ni herramientas de rastreo. No hay anuncios en la app. No se comparten datos con anunciantes, intermediarios de datos ni terceros. No solicitamos la Transparencia de Seguimiento de Apps porque no hay nada que rastrear." },
      { title: "Conforme con COPPA, RGPD-K y DSG suiza", content: "Nokuhiro cumple plenamente con COPPA, RGPD-K y la Ley Federal Suiza de Protección de Datos (nFADP/DSG). Se requiere consentimiento parental verificable antes de crear cualquier perfil infantil. Los niños no pueden crear cuentas, modificar configuraciones de privacidad ni acceder a controles parentales." },
      { title: "Funciones de IA", content: "Las funciones de IA generan resúmenes de progreso personalizados y recomendaciones de hábitos para los padres. El servicio de IA procesa datos solo para generar respuestas y no almacena datos después. Las funciones de IA se pueden desactivar en cualquier momento en la configuración." },
      { title: "Control parental total", content: "Los padres pueden revisar, exportar o eliminar todos los datos infantiles en cualquier momento directamente en la App. Puedes solicitar la eliminación de la cuenta en cualquier momento, tras lo cual todos los datos del servidor se eliminan permanentemente en 30 días." },
    ],
    contactText: "Para consultas de privacidad, solicitudes de eliminación de datos o cualquier inquietud, contáctanos en",
  },
  footer: {
    desc: "Construcción de hábitos basada en la ciencia para niños de 3 a 12 años. Transforma las rutinas diarias en aventuras heroicas.",
    product: "Producto",
    theScience: "La ciencia",
    scienceLinks: [
      { label: "Investigación conductual", href: "#science" },
      { label: "Mentalidad de crecimiento", href: "#science" },
      { label: "Privacidad y seguridad", href: "/privacy" },
      { label: "Diseño anti-adicción", href: "#science" },
    ],
    support: "Soporte",
    supportLinks: [
      { label: "Contacto", href: "mailto:hello@nokuhiro.com" },
      { label: "FAQ", href: "/faq" },
      { label: "Política de privacidad", href: "/privacy" },
      { label: "Términos de servicio", href: "/terms" },
    ],
    followUs: "Síguenos",
    instagram: "Instagram",
    copyright: "Nokuhiro. Todos los derechos reservados.",
    coppa: "Conforme con COPPA 2026",
    gdpr: "Conforme con RGPD-K",
  },
  heroCro: {
    heading: "Menos repetir. Rutinas m\u00e1s tranquilas.",
    subheading: "Nokuhiro convierte cepillarse los dientes, los deberes, ordenar, la hora de dormir y los h\u00e1bitos diarios en misiones juguetonas, para que los ni\u00f1os ganen independencia y los padres se repitan menos.",
    ctaIos: "Descargar en el App Store",
    ctaAndroid: "Disponible en Google Play",
    trustLine: "Compra \u00fanica. Sin anuncios. Sin suscripciones. Dise\u00f1ado para ni\u00f1os de 3\u201312 a\u00f1os.",
  },
  trustBar: {
    ages: "Edades 3\u201312",
    oneTime: "Compra \u00fanica",
    noAds: "Sin anuncios ni suscripciones",
    quickRoutines: "Check-ins de rutina r\u00e1pidos",
  },
  howItWorksCro: {
    label: "C\u00d3MO FUNCIONA",
    heading: "De rutina a aventura en 3 simples pasos.",
    steps: [
      { icon: "1", title: "Crea la rutina", desc: "Los padres eligen h\u00e1bitos como cepillarse los dientes, vestirse, leer, ordenar, deberes o actos de amabilidad." },
      { icon: "2", title: "Tu hijo sigue la misi\u00f3n", desc: "Los ni\u00f1os completan pasos visuales con su compa\u00f1ero y ganan progreso en el mapa de misiones." },
      { icon: "3", title: "Celebra el progreso real", desc: "Insignias, XP y recompensas familiares ayudan a los ni\u00f1os a sentirse orgullosos de h\u00e1bitos reales." },
    ],
  },
  productClarity: {
    label: "VELO EN ACCI\u00d3N",
    heading: "Mira exactamente lo que ve tu hijo.",
    subtitle: "Pantallas reales de la app mostrando c\u00f3mo Nokuhiro gu\u00eda a tu hijo en las rutinas diarias.",
    features: [
      { title: "Elige h\u00e1bitos apropiados para la edad", desc: "Configura rutinas adaptadas a la edad y necesidades de tu hijo." },
      { title: "Gu\u00eda rutinas con pasos visuales", desc: "Los ni\u00f1os siguen pasos de misi\u00f3n claros e ilustrados." },
      { title: "Los ni\u00f1os completan misiones solos", desc: "Tu hijo avanza en las rutinas de forma independiente." },
      { title: "Sigue el progreso sin recordatorios constantes", desc: "Observa c\u00f3mo se desarrollan los h\u00e1bitos con el tiempo." },
      { title: "Celebra la constancia en familia", desc: "Recompensa logros del mundo real juntos." },
    ],
  },
  parentPain: {
    label: "EL PROBLEMA",
    heading: "Las rutinas diarias no deber\u00edan sentirse como una batalla.",
    beforeTitle: "Sin Nokuhiro",
    afterTitle: "Con Nokuhiro",
    before: [
      "Repetir lo mismo una y otra vez",
      "Los ni\u00f1os pierden el enfoque",
      "Ma\u00f1anas apresuradas",
      "Negociaciones a la hora de dormir",
      "Padres frustrados",
    ],
    after: [
      "Pasos visuales claros",
      "M\u00e1s independencia infantil",
      "Menos recordatorios",
      "Transiciones m\u00e1s tranquilas",
      "Progreso del que los ni\u00f1os pueden sentirse orgullosos",
    ],
  },
  childMotivation: {
    label: "PARA NI\u00d1OS",
    heading: "Por qu\u00e9 los ni\u00f1os quieren volver.",
    subtitle: "Nokuhiro usa motivaci\u00f3n l\u00fadica para que los h\u00e1bitos reales se sientan visibles, alcanzables y gratificantes. Los ni\u00f1os son guiados por compa\u00f1eros, mapas de misiones, XP, insignias y peque\u00f1os logros \u2014 sin convertir las rutinas en tiempo de pantalla interminable.",
    items: [
      { icon: "\uD83E\uDD81", title: "Compa\u00f1eros animales", desc: "Un gu\u00eda amigable que crece con tu hijo." },
      { icon: "\uD83D\uDDFA\uFE0F", title: "Mapa de misiones", desc: "Progreso visual a trav\u00e9s de cada rutina." },
      { icon: "\u2B50", title: "XP e insignias", desc: "Gana recompensas por completar h\u00e1bitos reales." },
      { icon: "\uD83D\uDCC8", title: "Progreso visual", desc: "Los ni\u00f1os ven lo lejos que han llegado." },
      { icon: "\uD83C\uDF89", title: "Celebraciones", desc: "Momentos de orgullo al completar misiones." },
      { icon: "\uD83C\uDFC6", title: "Recompensas familiares", desc: "Los padres establecen recompensas reales por la constancia." },
    ],
  },
  parentValue: {
    label: "PARA PADRES",
    heading: "Lo que los padres recuperan.",
    benefits: [
      "Menos repetir",
      "Menos batallas de rutina",
      "M\u00e1s independencia",
      "Ma\u00f1anas m\u00e1s claras",
      "Hora de dormir m\u00e1s tranquila",
      "Mejor constancia en h\u00e1bitos",
      "Un sistema para m\u00faltiples rutinas",
    ],
    quote: "Las rutinas matutinas eran la parte m\u00e1s estresante de nuestro d\u00eda. Sent\u00eda que repet\u00eda las mismas instrucciones una y otra vez. Con Nokuhiro, nuestro hijo puede ver qu\u00e9 viene despu\u00e9s y completar los pasos de forma m\u00e1s independiente. Ha hecho nuestras ma\u00f1anas m\u00e1s tranquilas y mucho menos emocionales.",
    quoteAttribution: "Padre/madre de un ni\u00f1o de 5 a\u00f1os",
    quote2: "Probamos tablas y recordatorios antes, pero normalmente perd\u00edan su efecto despu\u00e9s de unos d\u00edas. Nokuhiro se siente diferente porque la rutina se convierte en algo que nuestro hijo quiere seguir. Las misiones, el compa\u00f1ero y el progreso hacen que los h\u00e1bitos diarios se sientan m\u00e1s positivos \u2014 y pasamos menos tiempo negociando.",
    quote2Attribution: "Padre/madre de dos ni\u00f1os, de 4 y 7 a\u00f1os",
  },
  paidValue: {
    label: "POR QU\u00c9 DE PAGO",
    heading: "Una compra. Sin suscripci\u00f3n. Sin anuncios.",
    body: "Nokuhiro est\u00e1 construido como una herramienta familiar enfocada, no como una trampa de atenci\u00f3n. Pagas una vez y obtienes una experiencia de rutina segura y tranquila sin anuncios, compras dentro de la app o presi\u00f3n de suscripci\u00f3n.",
    points: [
      "Cuesta menos que muchos planes familiares o un peque\u00f1o juguete",
      "Ayuda con rutinas que los padres enfrentan cada d\u00eda",
      "Sin suscripci\u00f3n mensual",
      "Dise\u00f1ado para uso diario repetido",
      "Construido para h\u00e1bitos reales, no entretenimiento pasivo",
    ],
    cta: "Descarga Nokuhiro hoy",
  },
  scienceCro: {
    label: "EL ENFOQUE",
    heading: "Basado en c\u00f3mo los ni\u00f1os realmente aprenden.",
    body: "Nokuhiro combina rutinas visuales, refuerzo positivo y motivaci\u00f3n l\u00fadica \u2014 enfoques respaldados por investigaci\u00f3n en desarrollo infantil. Interacciones cortas y con prop\u00f3sito ayudan a los ni\u00f1os a construir h\u00e1bitos reales sin crear dependencia del tiempo de pantalla.",
  },
  privacySafety: {
    label: "PRIVACIDAD Y SEGURIDAD",
    heading: "Construido para familias. Dise\u00f1ado con privacidad en mente.",
    items: [
      { icon: "\uD83D\uDEAB", title: "Sin publicidad", desc: "Tu familia nunca ver\u00e1 anuncios en Nokuhiro." },
      { icon: "\uD83D\uDC64", title: "Sin funciones sociales para ni\u00f1os", desc: "Sin chat, sin listas de amigos, sin perfiles p\u00fablicos." },
      { icon: "\uD83D\uDD12", title: "Sin rastreadores publicitarios", desc: "Ninguna red publicitaria rastrea a tu hijo." },
      { icon: "\uD83D\uDCE6", title: "Sin venta de datos familiares", desc: "Los datos de tu familia nunca se venden." },
      { icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67", title: "Experiencia gestionada por padres", desc: "Los padres controlan rutinas, recompensas y ajustes." },
      { icon: "\u23F1\uFE0F", title: "Dise\u00f1ado para uso breve y con prop\u00f3sito", desc: "Check-ins r\u00e1pidos de rutina, no tiempo de pantalla interminable." },
    ],
  },
  founder: {
    label: "NUESTRA HISTORIA",
    heading: "Creado por padres que tambi\u00e9n lo necesitaban.",
    body: "Nokuhiro fue creado por padres que conoc\u00edan el estr\u00e9s de repetir las mismas instrucciones cada d\u00eda. Lo que empez\u00f3 como un simple sistema familiar se convirti\u00f3 en una app dise\u00f1ada para hacer las rutinas m\u00e1s tranquilas, claras y agradables.",
  },
  useCases: {
    label: "CASOS DE USO",
    heading: "Funciona para las rutinas que los padres repiten cada d\u00eda.",
    items: [
      { title: "Rutina matutina", from: "Recordatorios constantes para prepararse", to: "Un camino de misi\u00f3n claro que tu hijo sigue paso a paso" },
      { title: "Cepillarse los dientes", from: "Discutir sobre cepillarse bien", to: "Una misi\u00f3n guiada con pasos visuales" },
      { title: "Vestirse", from: "Ma\u00f1anas distra\u00eddas y lentas", to: "Pasos visuales que mantienen a los ni\u00f1os en el camino" },
      { title: "Deberes", from: "Resistencia y procrastinaci\u00f3n", to: "Una misi\u00f3n estructurada con recompensas de progreso" },
      { title: "Lectura", from: "Dificultad para construir el h\u00e1bito", to: "Una misi\u00f3n diaria que celebra la constancia" },
      { title: "Ordenar", from: "Peticiones repetidas de limpiar", to: "Una rutina clara que los ni\u00f1os pueden seguir solos" },
      { title: "Hora de dormir", from: "Negociaciones de cinco minutos m\u00e1s", to: "Un camino de misi\u00f3n tranquilo para seguir paso a paso" },
      { title: "Tareas del hogar", from: "Ignorar peticiones de ayudar en casa", to: "Una misi\u00f3n que hace que ayudar se sienta como un logro" },
      { title: "Actos de amabilidad", from: "Dificultad para construir h\u00e1bitos de empat\u00eda", to: "Una misi\u00f3n diaria que celebra peque\u00f1os actos de bondad" },
    ],
  },
  proof: {
    label: "FAMILIAS REALES",
    heading: "Construido alrededor de desaf\u00edos reales de rutinas familiares.",
    quote1: "Las rutinas matutinas eran la parte m\u00e1s estresante de nuestro d\u00eda. Sent\u00eda que repet\u00eda las mismas instrucciones una y otra vez. Con Nokuhiro, nuestro hijo puede ver qu\u00e9 viene despu\u00e9s y completar los pasos de forma m\u00e1s independiente. Ha hecho nuestras ma\u00f1anas m\u00e1s tranquilas y mucho menos emocionales.",
    quote1Attribution: "Padre/madre de un ni\u00f1o de 5 a\u00f1os",
    quote2: "Probamos tablas y recordatorios antes, pero normalmente perd\u00edan su efecto despu\u00e9s de unos d\u00edas. Nokuhiro se siente diferente porque la rutina se convierte en algo que nuestro hijo quiere seguir. Las misiones, el compa\u00f1ero y el progreso hacen que los h\u00e1bitos diarios se sientan m\u00e1s positivos \u2014 y pasamos menos tiempo negociando.",
    quote2Attribution: "Padre/madre de dos ni\u00f1os, de 4 y 7 a\u00f1os",
  },
  faqCro: {
    label: "FAQ",
    heading: "Preguntas frecuentes de padres.",
    items: [
      { q: "\u00bfEs solo m\u00e1s tiempo de pantalla?", a: "No. Nokuhiro est\u00e1 dise\u00f1ado para check-ins de rutina cortos y con prop\u00f3sito que ayudan a los ni\u00f1os a completar h\u00e1bitos del mundo real." },
      { q: "\u00bfNokuhiro es una suscripci\u00f3n?", a: "No. Nokuhiro es una compra \u00fanica." },
      { q: "\u00bfPara qu\u00e9 edades es?", a: "Nokuhiro est\u00e1 dise\u00f1ado para ni\u00f1os de 3\u201312 a\u00f1os." },
      { q: "\u00bfFunciona para rutinas matutinas y de dormir?", a: "S\u00ed. Los padres pueden crear rutinas para ma\u00f1anas, hora de dormir, tareas, deberes, cepillarse los dientes, ordenar, leer y m\u00e1s." },
      { q: "\u00bfPueden usarlo varios ni\u00f1os?", a: "S\u00ed. Puedes crear perfiles separados para cada ni\u00f1o de tu familia." },
      { q: "\u00bfFunciona sin conexi\u00f3n?", a: "Las funciones principales de rutina funcionan sin conexi\u00f3n. Se necesita internet para sincronizar el progreso entre dispositivos." },
      { q: "\u00bfNokuhiro usa anuncios?", a: "No. Nokuhiro est\u00e1 dise\u00f1ado sin anuncios." },
      { q: "\u00bfRastrea a mi hijo para publicidad?", a: "No. Nokuhiro no usa rastreadores publicitarios." },
      { q: "\u00bfPor qu\u00e9 es de pago?", a: "Porque Nokuhiro est\u00e1 construido como una herramienta familiar enfocada sin anuncios, presi\u00f3n de suscripci\u00f3n o monetizaci\u00f3n de trampa de atenci\u00f3n." },
    ],
  },
  finalCta: {
    heading: "\u00bfListo para rutinas m\u00e1s tranquilas?",
    ctaIos: "Descargar en el App Store",
    ctaAndroid: "Disponible en Google Play",
  },
  sticky: {
    label: "Rutinas m\u00e1s tranquilas",
    ctaIos: "App Store",
    ctaAndroid: "Google Play",
  },
};

const it: Translations = {
  nav: {
    features: "Funzionalità",
    howItWorks: "Come funziona",
    companions: "Compagni",
    science: "Scienza",
    pricing: "Prezzo",
    download: "Scarica ora",
    downloadNow: "Scarica ora",
  },
  betaSignup: {
    heading1: "Unisciti alla ",
    heading2: "Beta chiusa",
    desc: "Nokuhiro è attualmente in fase di beta test chiusa. Iscriviti per essere tra le prime famiglie a vivere l'avventura.",
    emailPlaceholder: "Il tuo indirizzo email",
    platformLabel: "Quale piattaforma?",
    ios: "iOS",
    android: "Android",
    both: "Entrambe",
    submitBtn: "Unisciti alla Beta",
    successMsg: "Ci sei! Ti avviseremo quando Nokuhiro sarà pronto per il download.",
    alreadyRegistered: "Sei già nella lista! Ti contatteremo presto.",
    errorMsg: "Qualcosa è andato storto. Per favore riprova.",
  },
  hero: {
    tagline: "",
    heading1: "Meno rimproveri.",
    heading2: "Più crescita.",
    subtitle: "L'app di abitudini nº 1 per bambini dai 3 ai 12 anni. Routine scientifiche che i bambini adorano — senza pubblicità, senza abbonamenti.",
    cta1: "Scarica ora",
    cta2: "Esplora le funzionalità",
    badge1: "Solo 5 {currency} — Senza pubblicità",
    badge2: "Acquisto unico",
    badge3: "COPPA e GDPR-K sicuro",
    badge4: "5 lingue",
  },
  preview: {
    label: "Incontra Nokuhiro",
    heading1: "Il viaggio eroico",
    heading2: " del tuo bambino inizia qui",
    desc: "Guidati da Nokuhiro, il leone eroe, i bambini creano il proprio avatar eroe, scelgono un animale compagno tra 11 personaggi unici e intraprendono missioni quotidiane. Ogni abitudine completata guadagna XP, fa salire di livello il loro eroe e li avvicina a ricompense reali stabilite dai genitori.",
    feature1Title: "Nokuhiro — La tua guida",
    feature1Desc: "Un coraggioso leone eroe che offre consigli, incoraggiamento e celebra ogni vittoria",
    feature2Title: "Avventure sulla mappa",
    feature2Desc: "Sentiero dorato tortuoso con traguardi, compagni e ricompense del tesoro",
    feature3Title: "Motore IA adattivo",
    feature3Desc: "Regola dinamicamente gli XP in base alla padronanza e alla crescita — sul dispositivo, privato",
  },
  features: {
    label: "Funzionalità potenti",
    heading: "Tutto ciò di cui un eroe ha bisogno",
    subtitle: "Basato su scienza comportamentale peer-reviewed, progettato per i bambini, fidato dai genitori.",
    items: [
      { title: "Abitudini basate su missioni", desc: "Le abitudini quotidiane diventano missioni eroiche su un sentiero dorato tortuoso. I bambini vedono i loro progressi come una vera avventura con traguardi regolari nei punti chiave di progresso." },
      { title: "Compagni viventi", desc: "Scegli tra 11 compagni animali unici che crescono, reagiscono e si evolvono in base alle abitudini del tuo bambino. Sono lì per il loro amico — non solo per i punti." },
      { title: "Motore IA adattivo", desc: "L'IA sul dispositivo regola dinamicamente i valori XP — premiando lo sforzo sulle abitudini trascurate e riducendo le ricompense su quelle padroneggiate per costruire vera motivazione intrinseca." },
      { title: "Missioni di squadra tra fratelli", desc: "I fratelli accumulano XP verso ricompense familiari condivise. Battaglie con i boss, sfide cooperative e celebrazioni congiunte — nessuna competizione tossica, solo lavoro di squadra." },
      { title: "Tre modalità per età", desc: "Esploratore (3–5) usa grandi icone colorate e interazioni semplici. Avventuriero (6–8) aggiunge gamification completa. Campione (9–12) introduce statistiche e autonomia. Un'app, tre esperienze." },
      { title: "Design privacy-first", desc: "Architettura server-first con cache locale. Zero tracciamento di terze parti. Conforme a COPPA 2026 e GDPR-K. Tutti i dati crittografati in transito e a riposo." },
    ],
  },
  howItWorks: {
    label: "Come funziona",
    heading: "Dal download all'eroe quotidiano",
    subtitle: "Iniziare richiede meno di due minuti. Ecco il viaggio.",
    steps: [
      { title: "I genitori configurano in 90 secondi", desc: "Aggiungi il nome e l'età del tuo bambino. Scegli tra suggerimenti intelligenti di abitudini con micro-abitudini predefinite. Imposta una ricompensa reale e la durata della missione." },
      { title: "Il bambino incontra Nokuhiro", desc: "Nokuhiro, il leone eroe, dà il benvenuto al tuo bambino nella foresta incantata. Scelgono il loro animale compagno e completano la prima missione — con una grande celebrazione." },
      { title: "Le missioni quotidiane creano abitudini", desc: "Ogni giorno, i bambini completano missioni di abitudini per guadagnare XP, far salire di livello il loro compagno e progredire lungo il sentiero dorato verso la ricompensa." },
      { title: "Monitorare i progressi insieme", desc: "I genitori monitorano i progressi attraverso la dashboard con approfondimenti, report settimanali e statistiche di completamento. Celebrate i traguardi in famiglia." },
    ],
  },
  companions: {
    label: "Scegli il tuo compagno",
    heading: "11 compagni d'avventura unici",
    subtitle: "Ogni compagno ha la propria personalità, voce e abilità speciali. Si evolvono attraverso 5 stadi — da Cucciolo a Leggenda.",
    evolutionTitle: "5 stadi di evoluzione",
    stages: ["Cucciolo", "Esploratore", "Avventuriero", "Campione", "Leggenda"],
    animals: ["Dinosauro", "Unicorno", "Coniglio", "Volpe", "Gufo", "Scimmia", "Elefante", "Leone", "Panda", "Orso", "Mucca"],
    styles: ["Audace e Entusiasta", "Magico e Sognatore", "Energico e Veloce", "Furbo e Strategico", "Saggio e Riflessivo", "Avventuroso e Divertente", "Gentile e Saggio", "Coraggioso e Fiero", "Giocoso e Gentile", "Forte e Determinato", "Calmo e Premuroso"],
    traits: ["Scava tesori bonus", "Lascia una scia arcobaleno", "Corre lungo il sentiero", "Trova scorciatoie nascoste", "Condivide curiosità dopo le abitudini", "Ha una giraffa come compagna", "Non dimentica mai una serie", "La criniera brilla con le serie", "Fa celebrazioni con capriole", "Mostra animazioni di forza", "Coltiva un giardino di fiori"],
  },
  science: {
    label: "Supportato dalla ricerca",
    heading: "Scienza, non espedienti",
    subtitle: "Ogni funzionalità è radicata nella scienza comportamentale peer-reviewed. Questa non è una lista di compiti con adesivi — è un sistema comportamentale di precisione mascherato da gioco d'avventura.",
    items: [
      { title: "Automaticità di 66 giorni", source: "Lally et al., 2010", desc: "La ricerca mostra che le abitudini richiedono una mediana di 66 giorni per diventare automatiche. I nostri cicli di missione sono calibrati a 28–42 giorni con traguardi regolari lungo il percorso." },
      { title: "Framework Tiny Habits", source: "BJ Fogg, Stanford", desc: "Ogni abitudine inizia micro — sotto 30 secondi. Dopo 7 giorni consecutivi, l'app suggerisce di espandere. Inizia in piccolo, costruisci fiducia, scala con l'approvazione dei genitori." },
      { title: "Feedback Growth Mindset", source: "Carol Dweck, Stanford", desc: "Ogni lode si concentra sullo sforzo, mai sull'abilità. «Hai lavorato così tanto!» costruisce resilienza. «Sei così intelligente!» crea fragilità. Ogni testo segue questo principio." },
      { title: "Prevenzione della sovragiustificazione", source: "Deci, Koestner & Ryan, 1999", desc: "Transizione motivazionale in tre fasi: gancio estrinseco → spostamento di competenza → ancoraggio identitario. Le abitudini padroneggiate diventano «Poteri dell'Eroe» — parte di chi sono." },
      { title: "Rinforzo variabile", source: "Skinner, 1957", desc: "Momenti di Successo Critico (1 su 8) forniscono XP bonus con animazioni spettacolari. L'imprevedibilità rende ogni completamento di abitudine emozionante." },
      { title: "Finestra critica: 3–9 anni", source: "Pressman et al., Brown University", desc: "I pattern delle abitudini si fissano entro i 9 anni. Questo rende 3–9 anni la finestra di intervento a più alto impatto. Nokuhiro è progettato per massimizzare questo periodo critico." },
    ],
    antiAddictionTitle: "Costruito per aiutare, non per agganciare",
    antiAddictionDesc: "L'app si conclude naturalmente dopo il completamento delle abitudini quotidiane — nessuno scorrimento infinito o cicli che creano dipendenza. Nessuna ansia da streak — saltare un giorno semplicemente azzera il contatore senza penalità. Nessuna meccanica di perdita — i bambini non perdono mai progressi. Nessun dark pattern, nessun FOMO, nessun confronto sociale. Sessione sana tipica: circa 2–5 minuti.",
  },
  pricing: {
    label: "Semplice e giusto",
    heading: "Paga una volta. Tienilo per sempre.",
    subtitle: "Un piccolo acquisto. Nessun abbonamento. Nessuna pubblicità. Nessun acquisto in-app. L'avventura completa è tua — per sempre.",
    badge: "App completa — Pagamento unico",
    alsoAvailable: (code) => code === "CHF" ? "Disponibile anche a 5 EUR / 5 USD" : code === "EUR" ? "Disponibile anche a 5 CHF / 5 USD" : "Disponibile anche a 5 CHF / 5 EUR",
    oneTime: "Acquisto unico — tuo per sempre",
    items: ["Abitudini illimitate", "Profili bambino multipli", "Tutti gli 11 animali compagni", "Sistema completo mappa missioni", "Motore IA adattivo", "Tre modalità per età", "Dashboard analitica genitori", "5 lingue incluse", "Modalità offline", "Nessuna pubblicità, mai", "Nessun abbonamento", "Nessun acquisto in-app"],
    cta: "Scarica ora",
    whyTitle: "Perché così conveniente?",
    whyDesc: "Crediamo che ogni bambino meriti buone abitudini. Un piccolo pagamento sblocca tutto — nessun paywall, nessun livello premium, nessuna tariffa ricorrente.",
  },
  downloadSection: {
    heading1: "Pronto a costruire",
    heading2: " abitudini per la vita?",
    desc: "Nokuhiro è ora disponibile su iOS e Android. Scaricalo oggi e inizia l'avventura per la tua famiglia.",
    languages: "Disponibile in <strong>inglese</strong>, <strong>tedesco</strong>, <strong>spagnolo</strong>, <strong>italiano</strong> e <strong>francese</strong>",
  },
  privacy: {
    label: "La sicurezza della tua famiglia",
    heading: "Informativa sulla privacy",
    sections: [
      { title: "Raccolta dati minima", content: "Nokuhiro raccoglie solo i dati necessari per fornire il servizio. Tutti i profili dei bambini, i dati sulle abitudini, i progressi e le informazioni sui compagni sono memorizzati in modo sicuro sui nostri server, crittografati in transito e a riposo. Non condividiamo mai dati con terze parti né li utilizziamo per pubblicità." },
      { title: "Zero tracciamento e nessuna pubblicità", content: "Non utilizziamo analisi di terze parti, SDK pubblicitari o strumenti di tracciamento. Non ci sono pubblicità nell'app. Nessun dato viene condiviso con inserzionisti, intermediari di dati o terze parti. Non richiediamo la Trasparenza del Tracciamento delle App perché non c'è nulla da tracciare." },
      { title: "Conforme a COPPA, GDPR-K e LPD svizzera", content: "Nokuhiro è pienamente conforme a COPPA, GDPR-K e alla Legge Federale Svizzera sulla Protezione dei Dati (nLPD/DSG). Il consenso parentale verificabile è richiesto prima della creazione di qualsiasi profilo bambino. I bambini non possono creare account, modificare le impostazioni sulla privacy o accedere ai controlli parentali." },
      { title: "Funzioni IA", content: "Le funzioni IA generano riepiloghi di progresso personalizzati e raccomandazioni sulle abitudini per i genitori. Il servizio IA elabora i dati solo per generare risposte e non conserva dati successivamente. Le funzioni IA possono essere disattivate in qualsiasi momento nelle impostazioni." },
      { title: "Controllo parentale completo", content: "I genitori possono rivedere, esportare o eliminare tutti i dati dei bambini in qualsiasi momento direttamente nell'App. Puoi richiedere l'eliminazione dell'account in qualsiasi momento, dopo di che tutti i dati del server vengono eliminati permanentemente entro 30 giorni." },
    ],
    contactText: "Per richieste sulla privacy, richieste di cancellazione dati o qualsiasi preoccupazione, contattaci a",
  },
  footer: {
    desc: "Costruzione di abitudini basata sulla scienza per bambini dai 3 ai 12 anni. Trasforma le routine quotidiane in avventure eroiche.",
    product: "Prodotto",
    theScience: "La scienza",
    scienceLinks: [
      { label: "Ricerca comportamentale", href: "#science" },
      { label: "Growth Mindset", href: "#science" },
      { label: "Privacy e sicurezza", href: "/privacy" },
      { label: "Design anti-dipendenza", href: "#science" },
    ],
    support: "Supporto",
    supportLinks: [
      { label: "Contattaci", href: "mailto:hello@nokuhiro.com" },
      { label: "FAQ", href: "/faq" },
      { label: "Informativa sulla privacy", href: "/privacy" },
      { label: "Termini di servizio", href: "/terms" },
    ],
    followUs: "Seguici",
    instagram: "Instagram",
    copyright: "Nokuhiro. Tutti i diritti riservati.",
    coppa: "Conforme COPPA 2026",
    gdpr: "Conforme GDPR-K",
  },
  heroCro: {
    heading: "Meno ripetere. Routine pi\u00f9 serene.",
    subheading: "Nokuhiro trasforma lavarsi i denti, i compiti, riordinare, l\u2019ora della nanna e le abitudini quotidiane in missioni giocose, cos\u00ec i bambini diventano pi\u00f9 indipendenti e i genitori si ripetono meno.",
    ctaIos: "Scarica dall\u2019App Store",
    ctaAndroid: "Disponibile su Google Play",
    trustLine: "Acquisto unico. Nessuna pubblicit\u00e0. Nessun abbonamento. Per bambini dai 3 ai 12 anni.",
  },
  trustBar: {
    ages: "Et\u00e0 3\u201312",
    oneTime: "Acquisto unico",
    noAds: "Nessuna pubblicit\u00e0 o abbonamento",
    quickRoutines: "Check-in rapidi delle routine",
  },
  howItWorksCro: {
    label: "COME FUNZIONA",
    heading: "Da routine ad avventura in 3 semplici passi.",
    steps: [
      { icon: "1", title: "Crea la routine", desc: "I genitori scelgono abitudini come lavarsi i denti, vestirsi, leggere, riordinare, compiti o atti di gentilezza." },
      { icon: "2", title: "Il tuo bambino segue la missione", desc: "I bambini completano passi visivi con il loro compagno e guadagnano progressi sulla mappa delle missioni." },
      { icon: "3", title: "Festeggia i progressi reali", desc: "Badge, XP e ricompense familiari aiutano i bambini a sentirsi orgogliosi delle abitudini reali." },
    ],
  },
  productClarity: {
    label: "GUARDALO IN AZIONE",
    heading: "Guarda esattamente cosa vede il tuo bambino.",
    subtitle: "Schermate reali dell\u2019app che mostrano come Nokuhiro guida il tuo bambino nelle routine quotidiane.",
    features: [
      { title: "Scegli abitudini adatte all\u2019et\u00e0", desc: "Imposta routine su misura per l\u2019et\u00e0 e le esigenze del tuo bambino." },
      { title: "Guida le routine con passi visivi", desc: "I bambini seguono passi di missione chiari e illustrati." },
      { title: "I bambini completano le missioni da soli", desc: "Il tuo bambino progredisce nelle routine in modo indipendente." },
      { title: "Monitora i progressi senza promemoria costanti", desc: "Osserva come le abitudini si sviluppano nel tempo." },
      { title: "Festeggia la costanza in famiglia", desc: "Premiate insieme i risultati del mondo reale." },
    ],
  },
  parentPain: {
    label: "IL PROBLEMA",
    heading: "Le routine quotidiane non dovrebbero sembrare una battaglia.",
    beforeTitle: "Senza Nokuhiro",
    afterTitle: "Con Nokuhiro",
    before: [
      "Ripetere la stessa cosa ancora e ancora",
      "I bambini perdono la concentrazione",
      "Mattine frenetiche",
      "Negoziazioni all\u2019ora della nanna",
      "Genitori frustrati",
    ],
    after: [
      "Passi visivi chiari",
      "Pi\u00f9 indipendenza del bambino",
      "Meno promemoria",
      "Transizioni pi\u00f9 serene",
      "Progressi di cui i bambini possono essere orgogliosi",
    ],
  },
  childMotivation: {
    label: "PER I BAMBINI",
    heading: "Perch\u00e9 i bambini vogliono tornare.",
    subtitle: "Nokuhiro usa la motivazione giocosa per rendere le abitudini reali visibili, raggiungibili e gratificanti. I bambini sono guidati da compagni, mappe delle missioni, XP, badge e piccole vittorie \u2014 senza trasformare le routine in tempo di schermo infinito.",
    items: [
      { icon: "\uD83E\uDD81", title: "Compagni animali", desc: "Una guida amichevole che cresce con il tuo bambino." },
      { icon: "\uD83D\uDDFA\uFE0F", title: "Mappa delle missioni", desc: "Progresso visivo attraverso ogni routine." },
      { icon: "\u2B50", title: "XP e badge", desc: "Guadagna ricompense completando abitudini reali." },
      { icon: "\uD83D\uDCC8", title: "Progresso visivo", desc: "I bambini vedono quanto sono arrivati lontano." },
      { icon: "\uD83C\uDF89", title: "Celebrazioni", desc: "Momenti di orgoglio dopo aver completato le missioni." },
      { icon: "\uD83C\uDFC6", title: "Ricompense familiari", desc: "I genitori impostano ricompense reali per la costanza." },
    ],
  },
  parentValue: {
    label: "PER I GENITORI",
    heading: "Cosa recuperano i genitori.",
    benefits: [
      "Meno ripetere",
      "Meno battaglie di routine",
      "Pi\u00f9 indipendenza",
      "Mattine pi\u00f9 chiare",
      "Ora della nanna pi\u00f9 serena",
      "Migliore costanza nelle abitudini",
      "Un sistema per pi\u00f9 routine",
    ],
    quote: "Le routine mattutine erano la parte pi\u00f9 stressante della nostra giornata. Sentivo di ripetere le stesse istruzioni pi\u00f9 e pi\u00f9 volte. Con Nokuhiro, nostro figlio pu\u00f2 vedere cosa viene dopo e completare i passaggi in modo pi\u00f9 indipendente. Ha reso le nostre mattine pi\u00f9 serene e molto meno emotive.",
    quoteAttribution: "Genitore di un bambino di 5 anni",
    quote2: "Abbiamo provato tabelle e promemoria prima, ma di solito perdevano il loro effetto dopo pochi giorni. Nokuhiro \u00e8 diverso perch\u00e9 la routine diventa qualcosa che nostro figlio vuole seguire. Le missioni, il compagno e i progressi rendono le abitudini quotidiane pi\u00f9 positive \u2014 e passiamo meno tempo a negoziare.",
    quote2Attribution: "Genitore di due bambini, 4 e 7 anni",
  },
  paidValue: {
    label: "PERCH\u00c9 A PAGAMENTO",
    heading: "Un acquisto. Nessun abbonamento. Nessuna pubblicit\u00e0.",
    body: "Nokuhiro \u00e8 costruito come uno strumento familiare focalizzato, non come una trappola per l\u2019attenzione. Paghi una volta e ottieni un\u2019esperienza di routine sicura e serena senza pubblicit\u00e0, acquisti in-app o pressione da abbonamento.",
    points: [
      "Costa meno di molte uscite familiari o un piccolo giocattolo",
      "Aiuta con le routine che i genitori affrontano ogni giorno",
      "Nessun abbonamento mensile",
      "Progettato per l\u2019uso quotidiano ripetuto",
      "Costruito per abitudini reali, non intrattenimento passivo",
    ],
    cta: "Scarica Nokuhiro oggi",
  },
  scienceCro: {
    label: "L\u2019APPROCCIO",
    heading: "Basato su come i bambini imparano davvero.",
    body: "Nokuhiro combina routine visive, rinforzo positivo e motivazione giocosa \u2014 approcci supportati dalla ricerca sullo sviluppo infantile. Interazioni brevi e mirate aiutano i bambini a costruire abitudini reali senza creare dipendenza dal tempo di schermo.",
  },
  privacySafety: {
    label: "PRIVACY E SICUREZZA",
    heading: "Costruito per le famiglie. Progettato con la privacy in mente.",
    items: [
      { icon: "\uD83D\uDEAB", title: "Nessuna pubblicit\u00e0", desc: "La tua famiglia non vedr\u00e0 mai pubblicit\u00e0 in Nokuhiro." },
      { icon: "\uD83D\uDC64", title: "Nessuna funzione sociale per bambini", desc: "Nessuna chat, nessuna lista amici, nessun profilo pubblico." },
      { icon: "\uD83D\uDD12", title: "Nessun tracker pubblicitario", desc: "Nessuna rete pubblicitaria traccia il tuo bambino." },
      { icon: "\uD83D\uDCE6", title: "Nessuna vendita di dati familiari", desc: "I dati della tua famiglia non vengono mai venduti." },
      { icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67", title: "Esperienza gestita dai genitori", desc: "I genitori controllano routine, ricompense e impostazioni." },
      { icon: "\u23F1\uFE0F", title: "Progettato per un uso breve e mirato", desc: "Check-in rapidi di routine, non tempo di schermo infinito." },
    ],
  },
  founder: {
    label: "LA NOSTRA STORIA",
    heading: "Creato da genitori che ne avevano bisogno anche loro.",
    body: "Nokuhiro \u00e8 stato creato da genitori che conoscevano lo stress di ripetere le stesse istruzioni ogni giorno. Quello che \u00e8 iniziato come un semplice sistema familiare \u00e8 diventato un\u2019app progettata per rendere le routine pi\u00f9 serene, chiare e piacevoli.",
  },
  useCases: {
    label: "CASI D\u2019USO",
    heading: "Funziona per le routine che i genitori ripetono ogni giorno.",
    items: [
      { title: "Routine mattutina", from: "Promemoria costanti per prepararsi", to: "Un percorso di missione chiaro che il tuo bambino segue passo dopo passo" },
      { title: "Lavarsi i denti", from: "Discutere sul lavarsi bene", to: "Una missione guidata con passi visivi" },
      { title: "Vestirsi", from: "Mattine distratte e lente", to: "Passi visivi che tengono i bambini in carreggiata" },
      { title: "Compiti", from: "Resistenza e procrastinazione", to: "Una missione strutturata con ricompense di progresso" },
      { title: "Lettura", from: "Difficolt\u00e0 a costruire l\u2019abitudine", to: "Una missione quotidiana che celebra la costanza" },
      { title: "Riordinare", from: "Richieste ripetute di pulire", to: "Una routine chiara che i bambini possono seguire da soli" },
      { title: "Ora della nanna", from: "Negoziazioni ancora cinque minuti", to: "Un percorso di missione rilassante da seguire passo dopo passo" },
      { title: "Faccende domestiche", from: "Ignorare le richieste di aiutare in casa", to: "Una missione che fa sentire l\u2019aiutare come un traguardo" },
      { title: "Atti di gentilezza", from: "Difficolt\u00e0 a costruire abitudini di empatia", to: "Una missione quotidiana che celebra piccoli atti di gentilezza" },
    ],
  },
  proof: {
    label: "FAMIGLIE REALI",
    heading: "Costruito attorno a sfide reali di routine familiari.",
    quote1: "Le routine mattutine erano la parte pi\u00f9 stressante della nostra giornata. Sentivo di ripetere le stesse istruzioni pi\u00f9 e pi\u00f9 volte. Con Nokuhiro, nostro figlio pu\u00f2 vedere cosa viene dopo e completare i passaggi in modo pi\u00f9 indipendente. Ha reso le nostre mattine pi\u00f9 serene e molto meno emotive.",
    quote1Attribution: "Genitore di un bambino di 5 anni",
    quote2: "Abbiamo provato tabelle e promemoria prima, ma di solito perdevano il loro effetto dopo pochi giorni. Nokuhiro \u00e8 diverso perch\u00e9 la routine diventa qualcosa che nostro figlio vuole seguire. Le missioni, il compagno e i progressi rendono le abitudini quotidiane pi\u00f9 positive \u2014 e passiamo meno tempo a negoziare.",
    quote2Attribution: "Genitore di due bambini, 4 e 7 anni",
  },
  faqCro: {
    label: "FAQ",
    heading: "Domande frequenti dai genitori.",
    items: [
      { q: "\u00c8 solo pi\u00f9 tempo di schermo?", a: "No. Nokuhiro \u00e8 progettato per check-in di routine brevi e mirati che aiutano i bambini a completare abitudini del mondo reale." },
      { q: "Nokuhiro \u00e8 un abbonamento?", a: "No. Nokuhiro \u00e8 un acquisto unico." },
      { q: "Per quali et\u00e0 \u00e8?", a: "Nokuhiro \u00e8 progettato per bambini dai 3 ai 12 anni." },
      { q: "Funziona per routine mattutine e della nanna?", a: "S\u00ec. I genitori possono creare routine per mattine, ora della nanna, faccende, compiti, lavarsi i denti, riordinare, leggere e altro." },
      { q: "Possono usarlo pi\u00f9 bambini?", a: "S\u00ec. Puoi creare profili separati per ogni bambino della tua famiglia." },
      { q: "Funziona offline?", a: "Le funzioni principali di routine funzionano offline. Serve una connessione internet per sincronizzare i progressi tra dispositivi." },
      { q: "Nokuhiro usa pubblicit\u00e0?", a: "No. Nokuhiro \u00e8 progettato senza pubblicit\u00e0." },
      { q: "Traccia mio figlio per la pubblicit\u00e0?", a: "No. Nokuhiro non usa tracker pubblicitari." },
      { q: "Perch\u00e9 \u00e8 a pagamento?", a: "Perch\u00e9 Nokuhiro \u00e8 costruito come uno strumento familiare focalizzato senza pubblicit\u00e0, pressione da abbonamento o monetizzazione trappola per l\u2019attenzione." },
    ],
  },
  finalCta: {
    heading: "Pronto per routine pi\u00f9 serene?",
    ctaIos: "Scarica dall\u2019App Store",
    ctaAndroid: "Disponibile su Google Play",
  },
  sticky: {
    label: "Routine pi\u00f9 serene",
    ctaIos: "App Store",
    ctaAndroid: "Google Play",
  },
};

const fr: Translations = {
  nav: {
    features: "Fonctionnalités",
    howItWorks: "Comment ça marche",
    companions: "Compagnons",
    science: "Science",
    pricing: "Prix",
    download: "Télécharger maintenant",
    downloadNow: "Télécharger maintenant",
  },
  betaSignup: {
    heading1: "Rejoignez la ",
    heading2: "Bêta fermée",
    desc: "Nokuhiro est actuellement en phase de test bêta fermée. Inscrivez-vous pour être parmi les premières familles à vivre l'aventure.",
    emailPlaceholder: "Votre adresse e-mail",
    platformLabel: "Quelle plateforme ?",
    ios: "iOS",
    android: "Android",
    both: "Les deux",
    submitBtn: "Rejoindre la Bêta",
    successMsg: "Vous y êtes ! Nous vous préviendrons quand Nokuhiro sera prêt à télécharger.",
    alreadyRegistered: "Vous êtes déjà sur la liste ! Nous vous contacterons bientôt.",
    errorMsg: "Quelque chose s'est mal passé. Veuillez réessayer.",
  },
  hero: {
    tagline: "",
    heading1: "Moins de rappels.",
    heading2: "Plus de progrès.",
    subtitle: "L'app d'habitudes nº 1 pour enfants de 3 à 12 ans. Des routines scientifiques que les enfants adorent — sans pub, sans abonnement.",
    cta1: "Télécharger maintenant",
    cta2: "Explorer les fonctionnalités",
    badge1: "Seulement 5 {currency} — Sans pub",
    badge2: "Achat unique",
    badge3: "COPPA et RGPD-K sûr",
    badge4: "5 langues",
  },
  preview: {
    label: "Rencontrez Nokuhiro",
    heading1: "Le voyage héroïque",
    heading2: " de votre enfant commence ici",
    desc: "Guidés par Nokuhiro, le lion héros, les enfants créent leur propre avatar de héros, choisissent un animal compagnon parmi 11 personnages uniques et se lancent dans des quêtes quotidiennes. Chaque habitude complétée rapporte des XP, fait monter de niveau leur héros et les rapproche de récompenses réelles fixées par les parents.",
    feature1Title: "Nokuhiro — Votre guide",
    feature1Desc: "Un brave lion héros qui offre des conseils, des encouragements et célèbre chaque victoire",
    feature2Title: "Aventures sur la carte",
    feature2Desc: "Chemin doré sinueux avec des jalons, des compagnons et des récompenses de trésor",
    feature3Title: "Moteur IA adaptatif",
    feature3Desc: "Ajuste dynamiquement les XP selon la maîtrise et la croissance — sur l'appareil, privé",
  },
  features: {
    label: "Fonctionnalités puissantes",
    heading: "Tout ce dont un héros a besoin",
    subtitle: "Basé sur la science comportementale évaluée par des pairs, conçu pour les enfants, approuvé par les parents.",
    items: [
      { title: "Habitudes basées sur des quêtes", desc: "Les habitudes quotidiennes deviennent des quêtes héroïques sur un chemin doré sinueux. Les enfants voient leurs progrès comme une vraie aventure avec des jalons réguliers aux points clés de progression." },
      { title: "Compagnons vivants", desc: "Choisissez parmi 11 compagnons animaux uniques qui grandissent, réagissent et évoluent selon les habitudes de votre enfant. Ils sont là pour leur ami — pas seulement pour des points." },
      { title: "Moteur IA adaptatif", desc: "L'IA sur l'appareil ajuste dynamiquement les valeurs XP — récompensant l'effort sur les habitudes négligées et réduisant les récompenses sur celles maîtrisées pour construire une vraie motivation intrinsèque." },
      { title: "Quêtes d'équipe entre frères et sœurs", desc: "Les frères et sœurs accumulent des XP vers des récompenses familiales partagées. Combats de boss, défis coopératifs et célébrations communes — pas de compétition toxique, que du travail d'équipe." },
      { title: "Trois modes d'âge", desc: "Explorateur (3–5) utilise de grandes icônes colorées et des interactions simples. Aventurier (6–8) ajoute la gamification complète. Champion (9–12) introduit les statistiques et l'autonomie. Une app, trois expériences." },
      { title: "Design confidentialité d'abord", desc: "Architecture server-first avec cache local. Zéro suivi tiers. Conforme COPPA 2026 et RGPD-K. Toutes les données chiffrées en transit et au repos." },
    ],
  },
  howItWorks: {
    label: "Comment ça marche",
    heading: "Du téléchargement au héros quotidien",
    subtitle: "Commencer prend moins de deux minutes. Voici le parcours.",
    steps: [
      { title: "Les parents configurent en 90 secondes", desc: "Ajoutez le nom et l'âge de votre enfant. Choisissez parmi des suggestions intelligentes d'habitudes avec des micro-habitudes par défaut. Définissez une récompense réelle et la durée de la quête." },
      { title: "L'enfant rencontre Nokuhiro", desc: "Nokuhiro, le lion héros, accueille votre enfant dans la forêt enchantée. Il choisit son animal compagnon et complète sa première quête — avec une grande célébration." },
      { title: "Les quêtes quotidiennes créent des habitudes", desc: "Chaque jour, les enfants complètent des quêtes d'habitudes pour gagner des XP, faire monter de niveau leur compagnon et progresser sur le chemin doré vers leur récompense." },
      { title: "Suivre les progrès ensemble", desc: "Les parents surveillent les progrès via le tableau de bord avec des insights, des rapports hebdomadaires et des statistiques de complétion. Célébrez les jalons en famille." },
    ],
  },
  companions: {
    label: "Choisissez votre compagnon",
    heading: "11 compagnons d'aventure uniques",
    subtitle: "Chaque compagnon a sa propre personnalité, voix et capacités spéciales. Ils évoluent à travers 5 stades — de Nouveau-né à Légende.",
    evolutionTitle: "5 stades d'évolution",
    stages: ["Nouveau-né", "Explorateur", "Aventurier", "Champion", "Légende"],
    animals: ["Dinosaure", "Licorne", "Lapin", "Renard", "Hibou", "Singe", "Éléphant", "Lion", "Panda", "Ours", "Vache"],
    styles: ["Audacieux et Enthousiaste", "Magique et Rêveur", "Énergique et Rapide", "Malin et Stratégique", "Sage et Réfléchi", "Aventurier et Amusant", "Doux et Sage", "Courageux et Fier", "Joueur et Doux", "Fort et Déterminé", "Calme et Bienveillant"],
    traits: ["Déterre des trésors bonus", "Laisse une traînée arc-en-ciel", "Court le long du chemin", "Trouve des raccourcis cachés", "Partage des anecdotes après les habitudes", "A une girafe comme acolyte", "N'oublie jamais une série", "La crinière brille avec les séries", "Fait des célébrations en culbutes", "Montre des animations de force", "Cultive un jardin de fleurs"],
  },
  science: {
    label: "Soutenu par la recherche",
    heading: "Science, pas gadgets",
    subtitle: "Chaque fonctionnalité est ancrée dans la science comportementale évaluée par des pairs. Ce n'est pas une liste de tâches avec des autocollants — c'est un système comportemental de précision déguisé en jeu d'aventure.",
    items: [
      { title: "Automaticité de 66 jours", source: "Lally et al., 2010", desc: "La recherche montre que les habitudes prennent une médiane de 66 jours pour devenir automatiques. Nos cycles de quête sont calibrés à 28–42 jours avec des jalons réguliers tout au long du parcours." },
      { title: "Cadre Tiny Habits", source: "BJ Fogg, Stanford", desc: "Chaque habitude commence micro — moins de 30 secondes. Après 7 jours consécutifs, l'app suggère d'élargir. Commencez petit, construisez la confiance, montez en puissance avec l'approbation des parents." },
      { title: "Feedback état d'esprit de croissance", source: "Carol Dweck, Stanford", desc: "Tout éloge se concentre sur l'effort, jamais sur la capacité. « Tu as travaillé si dur ! » construit la résilience. « Tu es si intelligent ! » crée la fragilité. Chaque texte suit ce principe." },
      { title: "Prévention de la surjustification", source: "Deci, Koestner & Ryan, 1999", desc: "Transition motivationnelle en trois étapes : crochet extrinsèque → décalage de compétence → ancrage identitaire. Les habitudes maîtrisées deviennent des « Pouvoirs de Héros » — partie de qui ils sont." },
      { title: "Renforcement variable", source: "Skinner, 1957", desc: "Les moments de Succès Critique (1 sur 8) délivrent des XP bonus avec des animations spectaculaires. L'imprévisibilité rend chaque complétion d'habitude excitante." },
      { title: "Fenêtre critique : 3–9 ans", source: "Pressman et al., Brown University", desc: "Les schémas d'habitudes se fixent vers 9 ans. Cela fait de 3–9 ans la fenêtre d'intervention à plus fort impact. Nokuhiro est conçu pour maximiser cette période critique." },
    ],
    antiAddictionTitle: "Conçu pour aider, pas pour accrocher",
    antiAddictionDesc: "L'app se termine naturellement après la complétion des habitudes quotidiennes — pas de défilement infini ni de boucles addictives. Pas d'anxiété de série — manquer un jour réinitialise simplement le compteur sans pénalité. Pas de mécaniques de perte — les enfants ne perdent jamais de progrès. Pas de dark patterns, pas de FOMO, pas de comparaison sociale. Session saine typique : environ 2–5 minutes.",
  },
  pricing: {
    label: "Simple et juste",
    heading: "Payez une fois. Gardez pour toujours.",
    subtitle: "Un petit achat. Pas d'abonnements. Pas de publicités. Pas d'achats in-app. L'aventure complète est à vous — pour toujours.",
    badge: "App complète — Paiement unique",
    alsoAvailable: (code) => code === "CHF" ? "Aussi disponible pour 5 EUR / 5 USD" : code === "EUR" ? "Aussi disponible pour 5 CHF / 5 USD" : "Aussi disponible pour 5 CHF / 5 EUR",
    oneTime: "Achat unique — à vous pour toujours",
    items: ["Habitudes illimitées", "Profils enfants multiples", "Les 11 animaux compagnons", "Système complet de carte de quête", "Moteur IA adaptatif", "Trois modes par âge", "Tableau de bord analytique parents", "5 langues incluses", "Mode hors ligne", "Pas de pub, jamais", "Pas d'abonnements", "Pas d'achats in-app"],
    cta: "Télécharger maintenant",
    whyTitle: "Pourquoi si abordable ?",
    whyDesc: "Nous croyons que chaque enfant mérite de bonnes habitudes. Un petit paiement débloque tout — pas de paywall, pas de niveaux premium, pas de frais récurrents.",
  },
  downloadSection: {
    heading1: "Prêt à construire",
    heading2: " des habitudes pour la vie ?",
    desc: "Nokuhiro est maintenant disponible sur iOS et Android. Téléchargez-le aujourd'hui et commencez l'aventure pour votre famille.",
    languages: "Disponible en <strong>anglais</strong>, <strong>allemand</strong>, <strong>espagnol</strong>, <strong>italien</strong> et <strong>français</strong>",
  },
  privacy: {
    label: "La sécurité de votre famille",
    heading: "Politique de confidentialité",
    sections: [
      { title: "Collecte minimale de données", content: "Nokuhiro ne collecte que les données nécessaires pour fournir le service. Tous les profils d'enfants, données d'habitudes, progrès et informations sur les compagnons sont stockés en toute sécurité sur nos serveurs, chiffrés en transit et au repos. Nous ne partageons jamais de données avec des tiers ni ne les utilisons pour la publicité." },
      { title: "Zéro suivi et pas de pub", content: "Nous n'utilisons aucune analyse tierce, SDK publicitaire ou outil de suivi. Il n'y a pas de publicité dans l'app. Aucune donnée n'est partagée avec des annonceurs, courtiers en données ou tiers. Nous ne demandons pas la Transparence du Suivi des Apps car il n'y a rien à suivre." },
      { title: "Conforme COPPA, RGPD-K et LPD suisse", content: "Nokuhiro est pleinement conforme à COPPA, RGPD-K et à la Loi Fédérale Suisse sur la Protection des Données (nLPD/DSG). Le consentement parental vérifiable est requis avant la création de tout profil enfant. Les enfants ne peuvent pas créer de comptes, modifier les paramètres de confidentialité ou accéder aux contrôles parentaux." },
      { title: "Fonctions IA", content: "Les fonctions IA génèrent des résumés de progrès personnalisés et des recommandations d'habitudes pour les parents. Le service IA traite les données uniquement pour générer des réponses et ne conserve aucune donnée ensuite. Les fonctions IA peuvent être désactivées à tout moment dans les paramètres." },
      { title: "Contrôle parental complet", content: "Les parents peuvent consulter, exporter ou supprimer toutes les données des enfants à tout moment directement dans l'App. Vous pouvez demander la suppression du compte à tout moment, après quoi toutes les données du serveur sont définitivement supprimées sous 30 jours." },
    ],
    contactText: "Pour les demandes de confidentialité, les demandes de suppression de données ou toute préoccupation, contactez-nous à",
  },
  footer: {
    desc: "Construction d'habitudes basée sur la science pour les enfants de 3 à 12 ans. Transformez les routines quotidiennes en aventures héroïques.",
    product: "Produit",
    theScience: "La science",
    scienceLinks: [
      { label: "Recherche comportementale", href: "#science" },
      { label: "État d'esprit de croissance", href: "#science" },
      { label: "Confidentialité et sécurité", href: "/privacy" },
      { label: "Design anti-addiction", href: "#science" },
    ],
    support: "Support",
    supportLinks: [
      { label: "Contactez-nous", href: "mailto:hello@nokuhiro.com" },
      { label: "FAQ", href: "/faq" },
      { label: "Politique de confidentialité", href: "/privacy" },
      { label: "Conditions d'utilisation", href: "/terms" },
    ],
    followUs: "Nous suivre",
    instagram: "Instagram",
    copyright: "Nokuhiro. Tous droits réservés.",
    coppa: "Conforme COPPA 2026",
    gdpr: "Conforme RGPD-K",
  },
  heroCro: {
    heading: "Moins r\u00e9p\u00e9ter. Des routines plus sereines.",
    subheading: "Nokuhiro transforme le brossage de dents, les devoirs, le rangement, le coucher et les habitudes quotidiennes en qu\u00eates ludiques, pour que les enfants gagnent en autonomie et que les parents se r\u00e9p\u00e8tent moins.",
    ctaIos: "T\u00e9l\u00e9charger sur l\u2019App Store",
    ctaAndroid: "Disponible sur Google Play",
    trustLine: "Achat unique. Sans publicit\u00e9. Sans abonnement. Con\u00e7u pour les enfants de 3 \u00e0 12 ans.",
  },
  trustBar: {
    ages: "\u00c2ges 3\u201312",
    oneTime: "Achat unique",
    noAds: "Sans publicit\u00e9 ni abonnement",
    quickRoutines: "Check-ins de routine rapides",
  },
  howItWorksCro: {
    label: "COMMENT \u00c7A MARCHE",
    heading: "De la routine \u00e0 l\u2019aventure en 3 \u00e9tapes simples.",
    steps: [
      { icon: "1", title: "Cr\u00e9ez la routine", desc: "Les parents choisissent des habitudes comme se brosser les dents, s\u2019habiller, lire, ranger, les devoirs ou des actes de gentillesse." },
      { icon: "2", title: "Votre enfant suit la qu\u00eate", desc: "Les enfants compl\u00e8tent des \u00e9tapes visuelles avec leur compagnon et gagnent des progr\u00e8s sur la carte des qu\u00eates." },
      { icon: "3", title: "C\u00e9l\u00e9brez les progr\u00e8s r\u00e9els", desc: "Badges, XP et r\u00e9compenses familiales aident les enfants \u00e0 \u00eatre fiers de leurs vraies habitudes." },
    ],
  },
  productClarity: {
    label: "VOYEZ-LE EN ACTION",
    heading: "Voyez exactement ce que voit votre enfant.",
    subtitle: "\u00c9crans r\u00e9els de l\u2019app montrant comment Nokuhiro guide votre enfant dans les routines quotidiennes.",
    features: [
      { title: "Choisissez des habitudes adapt\u00e9es \u00e0 l\u2019\u00e2ge", desc: "Configurez des routines adapt\u00e9es \u00e0 l\u2019\u00e2ge et aux besoins de votre enfant." },
      { title: "Guidez les routines avec des \u00e9tapes visuelles", desc: "Les enfants suivent des \u00e9tapes de qu\u00eate claires et illustr\u00e9es." },
      { title: "Les enfants compl\u00e8tent les qu\u00eates seuls", desc: "Votre enfant progresse dans les routines de mani\u00e8re autonome." },
      { title: "Suivez les progr\u00e8s sans rappels constants", desc: "Observez comment les habitudes se d\u00e9veloppent au fil du temps." },
      { title: "C\u00e9l\u00e9brez la r\u00e9gularit\u00e9 en famille", desc: "R\u00e9compensez ensemble les r\u00e9ussites du monde r\u00e9el." },
    ],
  },
  parentPain: {
    label: "LE PROBL\u00c8ME",
    heading: "Les routines quotidiennes ne devraient pas ressembler \u00e0 une bataille.",
    beforeTitle: "Sans Nokuhiro",
    afterTitle: "Avec Nokuhiro",
    before: [
      "R\u00e9p\u00e9ter la m\u00eame chose encore et encore",
      "Les enfants perdent leur concentration",
      "Matins press\u00e9s",
      "N\u00e9gociations au coucher",
      "Parents frustr\u00e9s",
    ],
    after: [
      "\u00c9tapes visuelles claires",
      "Plus d\u2019autonomie de l\u2019enfant",
      "Moins de rappels",
      "Transitions plus sereines",
      "Des progr\u00e8s dont les enfants peuvent \u00eatre fiers",
    ],
  },
  childMotivation: {
    label: "POUR LES ENFANTS",
    heading: "Pourquoi les enfants veulent revenir.",
    subtitle: "Nokuhiro utilise la motivation ludique pour rendre les vraies habitudes visibles, atteignables et gratifiantes. Les enfants sont guid\u00e9s par des compagnons, des cartes de qu\u00eates, des XP, des badges et de petites victoires \u2014 sans transformer les routines en temps d\u2019\u00e9cran interminable.",
    items: [
      { icon: "\uD83E\uDD81", title: "Compagnons animaux", desc: "Un guide amical qui grandit avec votre enfant." },
      { icon: "\uD83D\uDDFA\uFE0F", title: "Carte des qu\u00eates", desc: "Progr\u00e8s visuel \u00e0 travers chaque routine." },
      { icon: "\u2B50", title: "XP et badges", desc: "Gagnez des r\u00e9compenses en compl\u00e9tant de vraies habitudes." },
      { icon: "\uD83D\uDCC8", title: "Progr\u00e8s visuel", desc: "Les enfants voient le chemin parcouru." },
      { icon: "\uD83C\uDF89", title: "C\u00e9l\u00e9brations", desc: "Moments de fiert\u00e9 apr\u00e8s avoir compl\u00e9t\u00e9 les qu\u00eates." },
      { icon: "\uD83C\uDFC6", title: "R\u00e9compenses familiales", desc: "Les parents fixent des r\u00e9compenses r\u00e9elles pour la r\u00e9gularit\u00e9." },
    ],
  },
  parentValue: {
    label: "POUR LES PARENTS",
    heading: "Ce que les parents r\u00e9cup\u00e8rent.",
    benefits: [
      "Moins de r\u00e9p\u00e9titions",
      "Moins de batailles de routine",
      "Plus d\u2019autonomie",
      "Matins plus clairs",
      "Coucher plus serein",
      "Meilleure r\u00e9gularit\u00e9 des habitudes",
      "Un syst\u00e8me pour plusieurs routines",
    ],
    quote: "Les routines matinales \u00e9taient la partie la plus stressante de notre journ\u00e9e. J\u2019avais l\u2019impression de r\u00e9p\u00e9ter les m\u00eames instructions encore et encore. Avec Nokuhiro, notre enfant peut voir ce qui vient ensuite et compl\u00e9ter les \u00e9tapes de mani\u00e8re plus autonome. Cela a rendu nos matins plus calmes et beaucoup moins \u00e9motionnels.",
    quoteAttribution: "Parent d\u2019un enfant de 5 ans",
    quote2: "Nous avons essay\u00e9 des tableaux et des rappels avant, mais ils perdaient g\u00e9n\u00e9ralement leur effet apr\u00e8s quelques jours. Nokuhiro est diff\u00e9rent parce que la routine devient quelque chose que notre enfant veut suivre. Les qu\u00eates, le compagnon et les progr\u00e8s rendent les habitudes quotidiennes plus positives \u2014 et nous passons moins de temps \u00e0 n\u00e9gocier.",
    quote2Attribution: "Parent de deux enfants, 4 et 7 ans",
  },
  paidValue: {
    label: "POURQUOI PAYANT",
    heading: "Un achat. Sans abonnement. Sans publicit\u00e9.",
    body: "Nokuhiro est con\u00e7u comme un outil familial cibl\u00e9, pas comme un pi\u00e8ge \u00e0 attention. Vous payez une fois et obtenez une exp\u00e9rience de routine s\u00fbre et sereine sans publicit\u00e9, achats int\u00e9gr\u00e9s ou pression d\u2019abonnement.",
    points: [
      "Co\u00fbte moins que beaucoup de sorties familiales ou un petit jouet",
      "Aide avec les routines que les parents affrontent chaque jour",
      "Pas d\u2019abonnement mensuel",
      "Con\u00e7u pour une utilisation quotidienne r\u00e9p\u00e9t\u00e9e",
      "Con\u00e7u pour de vraies habitudes, pas du divertissement passif",
    ],
    cta: "T\u00e9l\u00e9chargez Nokuhiro aujourd\u2019hui",
  },
  scienceCro: {
    label: "L\u2019APPROCHE",
    heading: "Bas\u00e9 sur la fa\u00e7on dont les enfants apprennent vraiment.",
    body: "Nokuhiro combine routines visuelles, renforcement positif et motivation ludique \u2014 des approches soutenues par la recherche en d\u00e9veloppement de l\u2019enfant. Des interactions courtes et cibl\u00e9es aident les enfants \u00e0 construire de vraies habitudes sans cr\u00e9er de d\u00e9pendance au temps d\u2019\u00e9cran.",
  },
  privacySafety: {
    label: "CONFIDENTIALIT\u00c9 ET S\u00c9CURIT\u00c9",
    heading: "Con\u00e7u pour les familles. Pens\u00e9 avec la confidentialit\u00e9 en t\u00eate.",
    items: [
      { icon: "\uD83D\uDEAB", title: "Aucune publicit\u00e9", desc: "Votre famille ne verra jamais de publicit\u00e9 dans Nokuhiro." },
      { icon: "\uD83D\uDC64", title: "Aucune fonction sociale pour enfants", desc: "Pas de chat, pas de listes d\u2019amis, pas de profils publics." },
      { icon: "\uD83D\uDD12", title: "Aucun tracker publicitaire", desc: "Aucun r\u00e9seau publicitaire ne suit votre enfant." },
      { icon: "\uD83D\uDCE6", title: "Aucune vente de donn\u00e9es familiales", desc: "Les donn\u00e9es de votre famille ne sont jamais vendues." },
      { icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67", title: "Exp\u00e9rience g\u00e9r\u00e9e par les parents", desc: "Les parents contr\u00f4lent les routines, r\u00e9compenses et param\u00e8tres." },
      { icon: "\u23F1\uFE0F", title: "Con\u00e7u pour un usage bref et cibl\u00e9", desc: "V\u00e9rifications rapides de routine, pas de temps d\u2019\u00e9cran interminable." },
    ],
  },
  founder: {
    label: "NOTRE HISTOIRE",
    heading: "Cr\u00e9\u00e9 par des parents qui en avaient besoin aussi.",
    body: "Nokuhiro a \u00e9t\u00e9 cr\u00e9\u00e9 par des parents qui connaissaient le stress de r\u00e9p\u00e9ter les m\u00eames instructions chaque jour. Ce qui a commenc\u00e9 comme un simple syst\u00e8me familial est devenu une app con\u00e7ue pour rendre les routines plus sereines, claires et agr\u00e9ables.",
  },
  useCases: {
    label: "CAS D\u2019UTILISATION",
    heading: "Fonctionne pour les routines que les parents r\u00e9p\u00e8tent chaque jour.",
    items: [
      { title: "Routine du matin", from: "Rappels constants pour se pr\u00e9parer", to: "Un chemin de qu\u00eate clair que votre enfant suit \u00e9tape par \u00e9tape" },
      { title: "Brossage de dents", from: "Disputes sur le brossage correct", to: "Une qu\u00eate guid\u00e9e avec des \u00e9tapes visuelles" },
      { title: "S\u2019habiller", from: "Matins distraits et lents", to: "\u00c9tapes visuelles qui gardent les enfants sur la bonne voie" },
      { title: "Devoirs", from: "R\u00e9sistance et procrastination", to: "Une qu\u00eate structur\u00e9e avec des r\u00e9compenses de progr\u00e8s" },
      { title: "Lecture", from: "Difficult\u00e9 \u00e0 construire l\u2019habitude", to: "Une qu\u00eate quotidienne qui c\u00e9l\u00e8bre la r\u00e9gularit\u00e9" },
      { title: "Ranger", from: "Demandes r\u00e9p\u00e9t\u00e9es de nettoyer", to: "Une routine claire que les enfants peuvent suivre seuls" },
      { title: "Coucher", from: "N\u00e9gociations encore cinq minutes", to: "Un chemin de qu\u00eate apaisant \u00e0 suivre \u00e9tape par \u00e9tape" },
      { title: "T\u00e2ches m\u00e9nag\u00e8res", from: "Ignorer les demandes d\u2019aider \u00e0 la maison", to: "Une qu\u00eate qui rend l\u2019aide gratifiante" },
      { title: "Actes de gentillesse", from: "Difficult\u00e9 \u00e0 construire des habitudes d\u2019empathie", to: "Une qu\u00eate quotidienne qui c\u00e9l\u00e8bre les petits actes de bont\u00e9" },
    ],
  },
  proof: {
    label: "VRAIES FAMILLES",
    heading: "Con\u00e7u autour de vrais d\u00e9fis de routines familiales.",
    quote1: "Les routines matinales \u00e9taient la partie la plus stressante de notre journ\u00e9e. J\u2019avais l\u2019impression de r\u00e9p\u00e9ter les m\u00eames instructions encore et encore. Avec Nokuhiro, notre enfant peut voir ce qui vient ensuite et compl\u00e9ter les \u00e9tapes de mani\u00e8re plus autonome. Cela a rendu nos matins plus calmes et beaucoup moins \u00e9motionnels.",
    quote1Attribution: "Parent d\u2019un enfant de 5 ans",
    quote2: "Nous avons essay\u00e9 des tableaux et des rappels avant, mais ils perdaient g\u00e9n\u00e9ralement leur effet apr\u00e8s quelques jours. Nokuhiro est diff\u00e9rent parce que la routine devient quelque chose que notre enfant veut suivre. Les qu\u00eates, le compagnon et les progr\u00e8s rendent les habitudes quotidiennes plus positives \u2014 et nous passons moins de temps \u00e0 n\u00e9gocier.",
    quote2Attribution: "Parent de deux enfants, 4 et 7 ans",
  },
  faqCro: {
    label: "FAQ",
    heading: "Questions fr\u00e9quentes des parents.",
    items: [
      { q: "Est-ce juste plus de temps d\u2019\u00e9cran ?", a: "Non. Nokuhiro est con\u00e7u pour des v\u00e9rifications de routine courtes et cibl\u00e9es qui aident les enfants \u00e0 compl\u00e9ter de vraies habitudes." },
      { q: "Nokuhiro est-il un abonnement ?", a: "Non. Nokuhiro est un achat unique." },
      { q: "Pour quels \u00e2ges ?", a: "Nokuhiro est con\u00e7u pour les enfants de 3 \u00e0 12 ans." },
      { q: "Fonctionne-t-il pour les routines du matin et du coucher ?", a: "Oui. Les parents peuvent cr\u00e9er des routines pour les matins, le coucher, les t\u00e2ches, les devoirs, le brossage de dents, le rangement, la lecture et plus." },
      { q: "Plusieurs enfants peuvent-ils l\u2019utiliser ?", a: "Oui. Vous pouvez cr\u00e9er des profils s\u00e9par\u00e9s pour chaque enfant de votre famille." },
      { q: "Fonctionne-t-il hors ligne ?", a: "Les fonctions principales de routine fonctionnent hors ligne. Une connexion internet est n\u00e9cessaire pour synchroniser les progr\u00e8s entre appareils." },
      { q: "Nokuhiro utilise-t-il de la publicit\u00e9 ?", a: "Non. Nokuhiro est con\u00e7u sans publicit\u00e9." },
      { q: "Suit-il mon enfant pour la publicit\u00e9 ?", a: "Non. Nokuhiro n\u2019utilise pas de trackers publicitaires." },
      { q: "Pourquoi est-il payant ?", a: "Parce que Nokuhiro est con\u00e7u comme un outil familial cibl\u00e9 sans publicit\u00e9, pression d\u2019abonnement ou mon\u00e9tisation pi\u00e8ge \u00e0 attention." },
    ],
  },
  finalCta: {
    heading: "Pr\u00eat pour des routines plus sereines ?",
    ctaIos: "T\u00e9l\u00e9charger sur l\u2019App Store",
    ctaAndroid: "Disponible sur Google Play",
  },
  sticky: {
    label: "Routines plus sereines",
    ctaIos: "App Store",
    ctaAndroid: "Google Play",
  },
};

export const translations: Record<Locale, Translations> = { en, de, es, it, fr };

export function getTranslations(locale: Locale): Translations {
  return translations[locale] || translations.en;
}
